import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type BeforeCapture,
  type DraggableProvided,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  CornerLeftUp,
  FolderInput,
  FolderPlus,
  GripVertical,
  List,
  ListFilter,
  ListTodo,
  ListTree,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Unlink,
  UserPlus,
  WrapText,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/Select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import {
  useAddProjectMembers,
  useAddTaskProjectLink,
  useArchiveProject,
  useCreateProjectSection,
  useCreateTask,
  useDeleteProject,
  useDeleteProjectSection,
  useDeleteTask,
  useMoveTaskToProject,
  useProject,
  useProjects,
  useProjectSections,
  useRemoveProjectMember,
  useRemoveTaskProjectLink,
  useReorderProjectSections,
  useReorderTasks,
  useSetTaskAssignees,
  useTasks,
  useUpdateProject,
  useUpdateProjectSection,
  useUpdateTask,
  type ProjectSection,
  type ProjectWithMembers,
  type TaskMove,
  type TaskWithAssignees,
} from '@/features/projects/hooks';
import { initialsOf, PeoplePicker } from '@/components/projects/PeoplePicker';
import { AssigneesPicker } from '@/components/projects/AssigneesPicker';
import { TaskDetailDrawer } from '@/components/projects/TaskDetailDrawer';
import { ProjectTaskCalendar } from '@/components/projects/ProjectTaskCalendar';
import {
  WorkloadHoursDraftInput,
  WorkloadHoursInput,
} from '@/components/projects/WorkloadHoursInput';
import { parseWorkloadHours } from '@/features/projects/workload';
import {
  PRIORITY_META,
  PRIORITY_ORDER,
  STATUS_META,
  STATUS_OPTION_ORDER,
  STATUS_ORDER,
  type TaskStatusOption,
  completionPercent,
  dueState,
  statusOptionMeta,
  taskStatusOption,
  taskStatusPatch,
} from '@/features/projects/display';
import {
  DEPARTMENTS,
  departmentMeta,
  isDepartmentSlug,
} from '@/features/projects/departments';
import {
  AvatarStack,
  CompletionBar,
  ProjectMonogram,
} from '@/components/projects/ProjectVisuals';
import {
  ActionSheet,
  ActionSheetItem,
  SheetChip,
  SheetSection,
} from '@/components/projects/MobileSheets';
import { MobileTaskList } from '@/components/projects/MobileTaskList';
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
} from '@/components/ui/BottomSheet';
import { Switch } from '@/components/ui/Switch';
import { useIsMobile } from '@/lib/useMediaQuery';
import type { TaskPriority, TaskStatus } from '@/types/database';

type SortKey =
  | 'name'
  | 'assignee'
  | 'due_date'
  | 'approximate_workload_hours'
  | 'priority'
  | 'status'
  | 'is_completed';
type SortDir = 'asc' | 'desc';

/** Grip + Task + Assignee + Due + Workload + Priority + Status + kebab + open */
const TASK_COL_COUNT = 9;

interface FilterState {
  assignees: Set<string>; // profile_ids; '__none__' marks "Unassigned"
  priorities: Set<TaskPriority>;
  statuses: Set<TaskStatus>;
  hideCompleted: boolean;
}

const EMPTY_FILTER: FilterState = {
  assignees: new Set(),
  priorities: new Set(),
  statuses: new Set(),
  hideCompleted: true,
};

type ViewMode = 'list' | 'calendar';

/**
 * Destination option for the "Move to project…" / "Add to project…"
 * submenus: every active project the current user can see, minus the
 * one being viewed.
 */
type ProjectOption = { id: string; name: string };

// Remembered across projects (and sessions) so a user who lives in the
// calendar view doesn't have to re-toggle it on every visit.
const VIEW_STORAGE_KEY = 'atlas:projects:viewMode';

// Remembered across projects (and sessions), matching the list/calendar
// view toggle. Default on so long task names stay fully visible; users
// who prefer compact single-line rows can turn wrapping off.
const WRAP_STORAGE_KEY = 'atlas:projects:wrapTaskName';

const WrapTaskNameContext = createContext(false);

// Filters are remembered per project (and across sessions) so navigating
// away — to another tab, project, or page — and coming back keeps the
// user's chosen assignee/priority/status/completed filters in place.
const FILTER_STORAGE_PREFIX = 'atlas:projects:filters:';

const freshEmptyFilter = (): FilterState => ({
  assignees: new Set(),
  priorities: new Set(),
  statuses: new Set(),
  hideCompleted: EMPTY_FILTER.hideCompleted,
});

function loadFilters(projectId: string): FilterState {
  if (!projectId) return freshEmptyFilter();
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_PREFIX + projectId);
    if (!raw) return freshEmptyFilter();
    const parsed = JSON.parse(raw) as {
      assignees?: string[];
      priorities?: TaskPriority[];
      statuses?: TaskStatus[];
      hideCompleted?: boolean;
    };
    return {
      assignees: new Set(parsed.assignees ?? []),
      priorities: new Set(parsed.priorities ?? []),
      statuses: new Set(parsed.statuses ?? []),
      hideCompleted:
        typeof parsed.hideCompleted === 'boolean'
          ? parsed.hideCompleted
          : EMPTY_FILTER.hideCompleted,
    };
  } catch {
    return freshEmptyFilter();
  }
}

function saveFilters(projectId: string, filters: FilterState) {
  if (!projectId) return;
  try {
    window.localStorage.setItem(
      FILTER_STORAGE_PREFIX + projectId,
      JSON.stringify({
        assignees: Array.from(filters.assignees),
        priorities: Array.from(filters.priorities),
        statuses: Array.from(filters.statuses),
        hideCompleted: filters.hideCompleted,
      }),
    );
  } catch {
    // localStorage unavailable (private mode etc.) — filters still work,
    // they just won't persist across navigation.
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ?? '';
  const navigate = useNavigate();
  // `?task=<id>` deep-links a task drawer (see the sync effects below).
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  const isMobile = useIsMobile();
  const projectQ = useProject(projectId);
  const tasksQ = useTasks(projectId);
  const sectionsQ = useProjectSections(projectId);
  const profilesQ = useProfiles();
  // Every active project the user can see — powers the "Move to
  // project…" / "Add to project…" submenus on each task row.
  const projectsQ = useProjects();
  const otherProjects: ProjectOption[] = useMemo(
    () =>
      (projectsQ.data ?? [])
        .filter((p) => p.id !== projectId)
        .map((p) => ({ id: p.id, name: p.name })),
    [projectsQ.data, projectId],
  );

  const [filters, setFilters] = useState<FilterState>(() =>
    loadFilters(projectId),
  );
  // Tracks which project the current `filters` belong to so the persist
  // effect never writes one project's filters under another's key while
  // a project switch is in flight.
  const filtersProjectRef = useRef(projectId);
  // When the route switches to a different project (the page component
  // stays mounted, only the param changes) load that project's saved
  // filters instead of carrying the previous project's over.
  useEffect(() => {
    if (filtersProjectRef.current !== projectId) {
      filtersProjectRef.current = projectId;
      setFilters(loadFilters(projectId));
    }
  }, [projectId]);
  // Persist filters whenever they change so they survive navigating away
  // and back (unmount/remount) as well as tab switches.
  useEffect(() => {
    saveFilters(filtersProjectRef.current, filters);
  }, [filters]);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'calendar'
        ? 'calendar'
        : 'list';
    } catch {
      return 'list';
    }
  });
  const changeView = (next: ViewMode) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private mode etc.) — view still works,
      // it just won't persist.
    }
  };
  const [wrapTaskName, setWrapTaskName] = useState(() => {
    try {
      const raw = window.localStorage.getItem(WRAP_STORAGE_KEY);
      if (raw === 'false') return false;
      if (raw === 'true') return true;
      return true;
    } catch {
      return true;
    }
  });
  const changeWrapTaskName = (next: boolean) => {
    setWrapTaskName(next);
    try {
      window.localStorage.setItem(WRAP_STORAGE_KEY, String(next));
    } catch {
      // localStorage unavailable (private mode etc.) — wrap still works,
      // it just won't persist.
    }
  };
  const [shareOpen, setShareOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Which task is showing in the side drawer. Stored as an ID (not a
  // full TaskWithAssignee) so optimistic updates to the underlying row
  // flow through to the drawer body via the live tasksQ.data lookup.
  // Seed from the `?task=` param so a deep link opens the drawer on the
  // very first render (the URL->state sync below only fires on *changes*).
  const [openTaskId, setOpenTaskId] = useState<string | null>(
    () => searchParams.get('task'),
  );
  // Section / subtask expand-state. We track the *collapsed* set so a
  // freshly created section / subtask group defaults to expanded
  // without us having to retro-fit the cache. Empty set == everything
  // expanded.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(),
  );
  // Subtasks default to *collapsed* (otherwise a project with 30
  // parents and 5 sub each would explode the row count). The set
  // tracks parent task ids that are currently expanded.
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<string>>(
    () => new Set(),
  );
  const [sectionToDelete, setSectionToDelete] = useState<ProjectSection | null>(
    null,
  );
  // True while a *task* row (top-level or subtask) is being dragged.
  // Used to hide the inline "Add subtask…" composer rows during the
  // drag — they're not Draggables, so the library can't shift them out
  // of the way and they'd visually collide with the displaced rows.
  const [isTaskDragActive, setIsTaskDragActive] = useState(false);
  // When the user picks up a parent whose subtasks are expanded we
  // collapse it for the duration of the drag (the children are
  // independent draggables and wouldn't travel with the parent). This
  // remembers which parent to re-expand once the drag settles.
  const reExpandAfterDragRef = useRef<string | null>(null);

  // Mutations shared by the page-level move handlers. We instantiate
  // them once here (rather than per-row) so we can compute the
  // destination position from the live tasks + sections caches in one
  // place, and so the optimistic update in useUpdateTask still flows
  // through the table renderer without us needing to re-hoist state.
  const updateTask = useUpdateTask();
  const reorderSections = useReorderProjectSections();
  const reorderTasks = useReorderTasks();
  const moveTaskMutation = useMoveTaskToProject();
  const addTaskLink = useAddTaskProjectLink();
  const removeTaskLink = useRemoveTaskProjectLink();

  // ---------- Move helpers ----------
  //
  // Section reordering goes through set_project_section_positions (a
  // batch RPC), and the task move operations are just patches against
  // section_id / parent_task_id / position written via useUpdateTask.
  // We compute the destination `position` client-side off the cached
  // tasks list so the row lands at the bottom of its new bucket
  // instead of inheriting a stale position from its old bucket (which
  // would otherwise create ordering collisions).
  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const list = sectionsQ.data ?? [];
    const idx = list.findIndex((s) => s.id === sectionId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const next = list.slice();
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    reorderSections.mutate(
      { projectId, orderedIds: next.map((s) => s.id) },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Reorder failed';
          toast.error(msg);
        },
      },
    );
  };

  const nextTopLevelPosition = (sectionId: string | null, excludeTaskId: string): number => {
    const tasks = tasksQ.data ?? [];
    let max = 0;
    for (const t of tasks) {
      if (t.id === excludeTaskId) continue;
      if (t.parent_task_id !== null) continue;
      if ((t.section_id ?? null) !== sectionId) continue;
      if (t.position > max) max = t.position;
    }
    return max + 1;
  };

  const nextSubtaskPosition = (parentId: string, excludeTaskId: string): number => {
    const tasks = tasksQ.data ?? [];
    let max = 0;
    for (const t of tasks) {
      if (t.id === excludeTaskId) continue;
      if (t.parent_task_id !== parentId) continue;
      if (t.position > max) max = t.position;
    }
    return max + 1;
  };

  const persistMove = (
    task: TaskWithAssignees,
    patch: { section_id: string | null; parent_task_id: string | null; position: number },
  ) => {
    updateTask.mutate(
      { id: task.id, projectId: task.project_id, patch },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Move failed';
          toast.error(msg);
        },
      },
    );
  };

  const moveTaskToSection = (task: TaskWithAssignees, sectionId: string | null) => {
    // Moving to a section always lands the task at the bottom of that
    // section's top-level bucket — and implicitly promotes a subtask
    // to a top-level row (parent_task_id cleared).
    const wasSubtask = task.parent_task_id !== null;
    const wasInSameSection = (task.section_id ?? null) === sectionId;
    if (!wasSubtask && wasInSameSection) return;
    persistMove(task, {
      section_id: sectionId,
      parent_task_id: null,
      position: nextTopLevelPosition(sectionId, task.id),
    });
  };

  const makeTaskSubtaskOf = (task: TaskWithAssignees, parentId: string) => {
    if (task.id === parentId) return;
    if (task.parent_task_id === parentId) return;
    persistMove(task, {
      // Subtasks don't carry their own section — the application
      // groups them under their parent. Mirrors the create-task
      // payload in useCreateTask which also nulls section_id when a
      // parent is provided.
      section_id: null,
      parent_task_id: parentId,
      position: nextSubtaskPosition(parentId, task.id),
    });
  };

  const promoteSubtaskToMain = (task: TaskWithAssignees) => {
    if (!task.parent_task_id) return;
    const tasks = tasksQ.data ?? [];
    const parent = tasks.find((t) => t.id === task.parent_task_id) ?? null;
    // Promote into the same section the parent currently lives in so
    // the row stays in the same visual region of the table.
    const targetSection = parent?.section_id ?? null;
    persistMove(task, {
      section_id: targetSection,
      parent_task_id: null,
      position: nextTopLevelPosition(targetSection, task.id),
    });
  };

  // ---------- Cross-project actions ----------
  //
  // "Move" re-homes the task (RPC move_task_to_project — subtasks
  // travel along); "Add" multi-homes it via task_project_links so it
  // shows up in another project while staying here; "Remove" deletes
  // the link from a project the task was added to.
  const moveTaskToProject = (task: TaskWithAssignees, destProjectId: string) => {
    const dest = otherProjects.find((p) => p.id === destProjectId);
    moveTaskMutation.mutate(
      {
        taskId: task.id,
        fromProjectId: projectId,
        toProjectId: destProjectId,
      },
      {
        onSuccess: () => {
          if (openTaskId === task.id) setOpenTaskId(null);
          toast.success(
            `Moved “${task.name}” to “${dest?.name ?? 'the other project'}”`,
          );
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Move failed';
          toast.error(msg);
        },
      },
    );
  };

  const addTaskToProject = (task: TaskWithAssignees, destProjectId: string) => {
    const dest = otherProjects.find((p) => p.id === destProjectId);
    addTaskLink.mutate(
      { taskId: task.id, projectId: destProjectId },
      {
        onSuccess: () => {
          toast.success(
            `“${task.name}” now also appears in “${dest?.name ?? 'the other project'}”`,
          );
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Could not add to project';
          toast.error(msg);
        },
      },
    );
  };

  // Only meaningful for linked rows on THIS page — removes the link
  // tying the task to the project being viewed. The task itself stays
  // intact in its home project.
  const removeTaskFromProject = (task: TaskWithAssignees) => {
    removeTaskLink.mutate(
      { taskId: task.id, projectId },
      {
        onSuccess: () => {
          if (openTaskId === task.id) setOpenTaskId(null);
          toast.success(
            `Removed “${task.name}” from this project. It still lives in “${task.project?.name ?? 'its home project'}”.`,
          );
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Could not remove';
          toast.error(msg);
        },
      },
    );
  };

  // ---------- Drag-and-drop ----------
  //
  // Two droppable kinds — the droppableId encodes which:
  //   * `bucket-<sectionId|__none__>` — every task row in a section:
  //     parents and (expanded) subtasks flattened into one list
  //   * `section-<id>`                — section headers, reorder only
  //
  // Because parents and subtasks share one droppable per section, a
  // drop can move a row anywhere in the hierarchy: reorder among
  // siblings, re-parent under a different task, promote a subtask to
  // top-level (in any section), or demote a childless top-level task
  // into a parent's subtask list. The drop slot's neighbours decide
  // what the drop means — see the TASK branch of onDragEnd (defined
  // below, after `grouped`, because it needs the filtered row lists).
  const onBeforeCapture = (before: BeforeCapture) => {
    const task = (tasksQ.data ?? []).find((t) => t.id === before.draggableId);
    if (!task) return; // section header drag — nothing to prepare
    setIsTaskDragActive(true);
    // Collapse an expanded parent for the duration of its drag: its
    // children are separate draggables and wouldn't travel with it.
    // onBeforeCapture runs before the library measures the rows, so
    // the collapsed layout is what gets captured.
    if (!task.parent_task_id && expandedSubtasks.has(task.id)) {
      reExpandAfterDragRef.current = task.id;
      setExpandedSubtasks((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  const finishTaskDrag = () => {
    setIsTaskDragActive(false);
    const reExpand = reExpandAfterDragRef.current;
    reExpandAfterDragRef.current = null;
    if (reExpand) {
      setExpandedSubtasks((prev) => new Set(prev).add(reExpand));
    }
  };

  const onSectionDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    // Each section header lives in its own single-item droppable
    // (so the headers can stay interleaved above their tasks). A
    // drop tells us which section's slot was the target via
    // `destination.droppableId` and which side via `destination.index`
    // (0 = before the target, 1 = after). We convert that to a
    // global insertion point in the ordered sections list.
    const sectionsList = sectionsQ.data ?? [];
    if (sectionsList.length < 2) return;
    const targetSectionId = sectionDroppableIdToSectionId(
      destination.droppableId,
    );
    if (!targetSectionId) return;
    const fromIdx = sectionsList.findIndex((s) => s.id === draggableId);
    if (fromIdx === -1) return;
    const next = sectionsList.slice();
    const [moved] = next.splice(fromIdx, 1);
    const targetIdxAfterRemoval = next.findIndex(
      (s) => s.id === targetSectionId,
    );
    // If we dropped onto our own droppable (the only one with a
    // matching id post-remove is gone), bail — that's a no-op.
    if (targetIdxAfterRemoval === -1) return;
    const insertIdx =
      targetIdxAfterRemoval + (destination.index === 0 ? 0 : 1);
    next.splice(insertIdx, 0, moved);
    // Skip if nothing actually changed (re-drop in the same spot).
    const same = next.every((s, i) => s.id === sectionsList[i]?.id);
    if (same) return;
    reorderSections.mutate(
      { projectId, orderedIds: next.map((s) => s.id) },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Reorder failed';
          toast.error(msg);
        },
      },
    );
  };

  // Member profiles drive both the assignee dropdown on each task row
  // and the "Members" section at the top of the page. We resolve the
  // full profile from the org-wide list (so we have email + avatar)
  // and fall back to the embedded `profiles` join for cases where the
  // user has been deactivated since being added.
  const allProfiles = profilesQ.data ?? [];
  const profileById = useMemo(() => {
    const map = new Map<string, (typeof allProfiles)[number]>();
    for (const p of allProfiles) map.set(p.id, p);
    return map;
    // allProfiles is recomputed every render but its content only
    // changes when profilesQ.data does — keying off length is enough
    // for the membership lookup we do here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesQ.data]);

  const memberProfiles = useMemo(() => {
    if (!projectQ.data) return [];
    return projectQ.data.project_members
      .map((m) => {
        const orgProfile = profileById.get(m.profile_id);
        if (orgProfile) {
          return {
            id: orgProfile.id,
            full_name: orgProfile.full_name,
            email: orgProfile.email,
            avatar_url: orgProfile.avatar_url,
          };
        }
        if (m.profiles) {
          return {
            id: m.profiles.id,
            full_name: m.profiles.full_name,
            email: m.profiles.email,
            avatar_url: m.profiles.avatar_url,
          };
        }
        return null;
      })
      .filter((p): p is NonNullable<typeof p> => !!p);
  }, [projectQ.data, profileById]);

  const isOwner =
    !!profile && !!projectQ.data && projectQ.data.created_by === profile.id;
  const isSuperAdmin = profile?.role === 'super_admin';
  const canManageMembers = isOwner || isSuperAdmin;
  // Any project member (i.e. anyone who can SELECT this project) can
  // close / reopen it via the set_project_archived RPC, even if they
  // didn't create it. Delete remains owner-only.
  const canArchive = !!projectQ.data;
  const canDelete = canManageMembers;
  // Name + department ride on the projects UPDATE policy (creator /
  // super_admin), same as every other piece of project metadata.
  const canEdit = canManageMembers;
  const isArchived = !!projectQ.data?.archived_at;

  // Group tasks by (section, parent) and apply filters / sort. We do
  // the entire pipeline client-side so the editing flow stays
  // optimistic — every keystroke would otherwise round-trip to filter
  // the server. The grouped structure is what the table renderer
  // walks: an ordered list of buckets (unsectioned first, then each
  // named section in `position` order), where each bucket carries its
  // own ordered list of parent rows and a map from parent id to the
  // ordered subtask rows beneath it.
  const grouped = useMemo(() => {
    const all = tasksQ.data ?? [];
    const sections = sectionsQ.data ?? [];

    const matchesFilters = (t: TaskWithAssignees): boolean => {
      if (filters.hideCompleted && t.is_completed) return false;
      if (filters.assignees.size > 0) {
        const wantsUnassigned = filters.assignees.has('__none__');
        const matchesUnassigned =
          wantsUnassigned && t.assignees.length === 0;
        const matchesPerson = t.assignees.some((a) =>
          filters.assignees.has(a.id),
        );
        if (!matchesUnassigned && !matchesPerson) return false;
      }
      if (filters.priorities.size > 0 && !filters.priorities.has(t.priority))
        return false;
      if (filters.statuses.size > 0 && !filters.statuses.has(t.status))
        return false;
      return true;
    };

    // Pass 1: split parents from subtasks, and stage subtask lists by
    // parent id. We don't filter subtasks here — they get filtered in
    // pass 3 so we can decide whether a parent should be kept as
    // "context" for matching children even if the parent itself
    // doesn't match the filter.
    //
    // Tasks whose home is ANOTHER project (linked in via
    // task_project_links — `project_id !== projectId`) are collected
    // separately: they render at the bottom of the unsectioned bucket
    // as non-draggable rows so they can't disturb this project's
    // drag-reorder math. Their subtasks still flow through
    // subtasksByParent so the expandable groups work.
    const parents: TaskWithAssignees[] = [];
    const linkedParentsAll: TaskWithAssignees[] = [];
    const subtasksByParent = new Map<string, TaskWithAssignees[]>();
    for (const t of all) {
      if (t.parent_task_id) {
        const arr = subtasksByParent.get(t.parent_task_id);
        if (arr) arr.push(t);
        else subtasksByParent.set(t.parent_task_id, [t]);
      } else if (t.project_id !== projectId) {
        linkedParentsAll.push(t);
      } else {
        parents.push(t);
      }
    }
    // Subtasks always render in their saved position order — they
    // don't participate in the parent column sort because the sort
    // controls live on the parent row header.
    for (const list of subtasksByParent.values()) {
      list.sort((a, b) => a.position - b.position);
    }

    // Pass 2: figure out which subtasks are visible under each
    // parent. A parent is "kept" if it matches the filter OR any of
    // its subtasks match the filter (otherwise filtering by an
    // unassigned assignee would silently hide a whole subtask group
    // whose parent was assigned).
    const visibleSubtasksByParent = new Map<string, TaskWithAssignees[]>();
    const parentHasMatch = new Set<string>();
    for (const p of [...parents, ...linkedParentsAll]) {
      const kids = subtasksByParent.get(p.id) ?? [];
      const visibleKids = kids.filter(matchesFilters);
      if (visibleKids.length > 0) parentHasMatch.add(p.id);
      visibleSubtasksByParent.set(p.id, visibleKids);
    }

    const visibleParents = parents.filter(
      (p) => matchesFilters(p) || parentHasMatch.has(p.id),
    );
    const linkedTasks = linkedParentsAll.filter(
      (p) => matchesFilters(p) || parentHasMatch.has(p.id),
    );
    if (sort) {
      linkedTasks.sort((a, b) => compareTasks(a, b, sort.key));
      if (sort.dir === 'desc') linkedTasks.reverse();
    }

    // Pass 3: bucket parents by section id. Unsectioned parents go
    // into the synthetic '__none__' bucket so the renderer can treat
    // them uniformly. Sort each bucket if a sort is active; otherwise
    // preserve the server-side (is_completed, position) ordering
    // that already flows out of useTasks.
    const bySection = new Map<string, TaskWithAssignees[]>();
    bySection.set('__none__', []);
    for (const s of sections) bySection.set(s.id, []);
    for (const p of visibleParents) {
      const key = p.section_id ?? '__none__';
      // A task could carry a section_id that points at a section we
      // don't have a query result for (e.g. mid-deletion race).
      // Drop it into the unsectioned bucket so it doesn't vanish.
      const bucket = bySection.get(key) ?? bySection.get('__none__')!;
      bucket.push(p);
    }
    if (sort) {
      for (const list of bySection.values()) {
        list.sort((a, b) => compareTasks(a, b, sort.key));
        if (sort.dir === 'desc') list.reverse();
      }
    }

    type Bucket = {
      key: string;
      section: ProjectSection | null;
      tasks: TaskWithAssignees[];
      totalTaskCount: number; // pre-filter parent count for the header
    };

    const totalParentsBySection = new Map<string, number>();
    for (const p of parents) {
      const k = p.section_id ?? '__none__';
      totalParentsBySection.set(k, (totalParentsBySection.get(k) ?? 0) + 1);
    }

    const buckets: Bucket[] = [];
    // Unsectioned bucket first so projects without any sections look
    // identical to today (no header, just rows).
    buckets.push({
      key: '__none__',
      section: null,
      tasks: bySection.get('__none__') ?? [],
      totalTaskCount: totalParentsBySection.get('__none__') ?? 0,
    });
    for (const s of sections) {
      buckets.push({
        key: s.id,
        section: s,
        tasks: bySection.get(s.id) ?? [],
        totalTaskCount: totalParentsBySection.get(s.id) ?? 0,
      });
    }

    const visibleTotal =
      visibleParents.length +
      linkedTasks.length +
      Array.from(visibleSubtasksByParent.values()).reduce(
        (acc, list) => acc + list.length,
        0,
      );
    const baseTotal = all.length;

    return {
      buckets,
      linkedTasks,
      visibleSubtasksByParent,
      subtasksByParent,
      visibleTotal,
      baseTotal,
    };
  }, [tasksQ.data, sectionsQ.data, filters, sort, projectId]);

  /**
   * The bucket's visible rows in render order (parents followed by the
   * expanded subtasks beneath each one). Must mirror SectionGroup's
   * flattening exactly so draggable indexes line up with the indexes
   * the drag library reports on drop.
   */
  const visibleBucketRows = (sectionId: string | null): FlatRowEntry[] => {
    const bucket = grouped.buckets.find(
      (b) => (b.section?.id ?? null) === sectionId,
    );
    if (!bucket) return [];
    return flatBucketRows(
      bucket.tasks,
      grouped.visibleSubtasksByParent,
      expandedSubtasks,
    );
  };

  // A task drop can land anywhere in any section's flattened list, so
  // the slot's neighbours decide what it means:
  //   * dropped directly above a subtask of P  → becomes a subtask of P
  //   * dropped at the end of P's subtask block → a dragged subtask
  //     joins P; a dragged top-level task lands after P, still top-level
  //   * anywhere else                           → top-level at that spot
  const onTaskDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    // Both ends must be bucket droppables. We check the prefix
    // explicitly because `bucketIdFromDroppableId` returns `null` for
    // both "not a bucket droppable" AND "the unsectioned bucket".
    if (
      !isBucketDroppableId(source.droppableId) ||
      !isBucketDroppableId(destination.droppableId)
    ) {
      return;
    }
    // Linked tasks (home in another project) render as non-draggable
    // rows outside the droppable's index space — keep them out of the
    // renumber math too so a reorder never rewrites a foreign task's
    // position/section.
    const allTasks = (tasksQ.data ?? []).filter(
      (t) => t.project_id === projectId,
    );
    const moved = allTasks.find((t) => t.id === draggableId);
    if (!moved) return;
    const dstBucket = bucketIdFromDroppableId(destination.droppableId);

    // Neighbours of the drop slot in the destination's visible list,
    // with the dragged row taken out (the library reports the index in
    // the post-removal list for same-droppable drags; for cross-bucket
    // drags the dragged row isn't in the destination list anyway).
    const dstRows = visibleBucketRows(dstBucket).filter(
      (r) => r.task.id !== draggableId,
    );
    const above =
      destination.index > 0 ? dstRows[destination.index - 1] : undefined;
    const below = dstRows[destination.index];

    const wasSubtask = moved.parent_task_id !== null;
    let targetParentId: string | null = null;
    if (below?.parentId) {
      // Landing right above an existing subtask → join that group.
      targetParentId = below.parentId;
    } else if (above?.parentId && wasSubtask) {
      // Landing at the end of a subtask block: a subtask joins the
      // group; a top-level task stays top-level (after the block).
      targetParentId = above.parentId;
    }

    if (targetParentId === moved.id) return;
    if (targetParentId && allTasks.some((t) => t.parent_task_id === moved.id)) {
      toast.error(
        'A task with subtasks cannot become a subtask. Move its subtasks out first.',
      );
      return;
    }

    const byPosition = (a: TaskWithAssignees, b: TaskWithAssignees) =>
      a.position - b.position;
    const moves: TaskMove[] = [];

    if (targetParentId) {
      // Destination: another task's subtask list. Insert relative to
      // the visible neighbour, then renumber the whole sibling list.
      const siblings = allTasks
        .filter((t) => t.parent_task_id === targetParentId && t.id !== moved.id)
        .sort(byPosition);
      let insertIdx = siblings.length;
      if (below?.parentId === targetParentId) {
        const i = siblings.findIndex((t) => t.id === below.task.id);
        if (i !== -1) insertIdx = i;
      } else if (above?.parentId === targetParentId) {
        const i = siblings.findIndex((t) => t.id === above.task.id);
        if (i !== -1) insertIdx = i + 1;
      }
      const nextSiblings = siblings.slice();
      nextSiblings.splice(insertIdx, 0, moved);
      nextSiblings.forEach((t, idx) =>
        moves.push({
          id: t.id,
          section_id: null,
          parent_task_id: targetParentId,
          position: idx + 1,
        }),
      );
    } else {
      // Destination: the bucket's top-level list. `below` here is
      // always top-level (a subtask below would have re-parented us).
      const siblings = parentsInBucket(allTasks, dstBucket).filter(
        (t) => t.id !== moved.id,
      );
      let insertIdx = siblings.length;
      if (below) {
        const i = siblings.findIndex((t) => t.id === below.task.id);
        if (i !== -1) insertIdx = i;
      } else if (above) {
        // The row above may be a subtask — then we land directly after
        // that subtask's parent block.
        const refId = above.parentId ?? above.task.id;
        const i = siblings.findIndex((t) => t.id === refId);
        if (i !== -1) insertIdx = i + 1;
      }
      const nextSiblings = siblings.slice();
      nextSiblings.splice(insertIdx, 0, moved);
      nextSiblings.forEach((t, idx) =>
        moves.push({
          id: t.id,
          section_id: dstBucket,
          parent_task_id: null,
          position: idx + 1,
        }),
      );
    }

    // Renumber the container the row left (when it's a different one)
    // so its sequence doesn't keep a hole.
    const oldParentId = moved.parent_task_id ?? null;
    const oldSection = moved.section_id ?? null;
    if (oldParentId) {
      if (oldParentId !== targetParentId) {
        allTasks
          .filter((t) => t.parent_task_id === oldParentId && t.id !== moved.id)
          .sort(byPosition)
          .forEach((t, idx) =>
            moves.push({
              id: t.id,
              section_id: null,
              parent_task_id: oldParentId,
              position: idx + 1,
            }),
          );
      }
    } else if (targetParentId !== null || oldSection !== dstBucket) {
      parentsInBucket(allTasks, oldSection)
        .filter((t) => t.id !== moved.id)
        .forEach((t, idx) =>
          moves.push({
            id: t.id,
            section_id: oldSection,
            parent_task_id: null,
            position: idx + 1,
          }),
        );
    }

    const changed = filterChangedMoves(moves, allTasks);
    if (changed.length === 0) return;
    reorderTasks.mutate(
      { projectId, moves: changed },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Move failed';
          toast.error(msg);
        },
      },
    );
  };

  const onDragEnd = (result: DropResult) => {
    finishTaskDrag();
    if (result.type === 'SECTION') onSectionDragEnd(result);
    else if (result.type === 'TASK') onTaskDragEnd(result);
  };

  const filterCount =
    filters.assignees.size + filters.priorities.size + filters.statuses.size;

  // Flat list of every task the current filters allow (parents +
  // visible subtasks) for the calendar view, which doesn't care about
  // section grouping — only due dates.
  const calendarTasks = useMemo(() => {
    const parents = grouped.buckets.flatMap((b) => b.tasks);
    const subs = Array.from(grouped.visibleSubtasksByParent.values()).flat();
    return [...parents, ...grouped.linkedTasks, ...subs];
  }, [grouped]);

  // Roll-up for the overview strip, ignoring the active filters. Only
  // tasks whose home is this project count (linked tasks belong to
  // their home project's numbers) so it matches the dashboard card.
  const taskStats = useMemo(() => {
    let total = 0;
    let done = 0;
    let overdue = 0;
    for (const t of tasksQ.data ?? []) {
      if (t.project_id !== projectId) continue;
      total += 1;
      if (t.is_completed) done += 1;
      else if (dueState(t.due_date) === 'overdue') overdue += 1;
    }
    return { total, done, open: total - done, overdue };
  }, [tasksQ.data, projectId]);

  // Pull the live task out of the cache for the drawer so inline edits
  // made elsewhere (the row, or the drawer itself) are reflected.
  const openTask = useMemo(() => {
    if (!openTaskId) return null;
    return (tasksQ.data ?? []).find((t) => t.id === openTaskId) ?? null;
  }, [openTaskId, tasksQ.data]);

  // Deep-link support: keep the open task in sync with a `?task=<id>`
  // URL param so notifications (and shared links) can open a specific
  // task's drawer. Two guarded effects give two-way sync without a loop
  // — each only acts when the URL and local state actually diverge.
  const taskParam = searchParams.get('task');
  // URL -> state: only react to *actual* changes in the `task` param
  // (external navigation, notifications, back/forward). We must NOT key
  // this off `openTaskId` — closing the drawer sets `openTaskId` to null
  // before the URL flushes, and an `openTaskId`-keyed effect would see
  // the still-stale `?task=` param and immediately re-open the drawer,
  // so it could never be closed. Tracking the last-seen param in a ref
  // lets us fire strictly on the URL side of the round-trip.
  const lastTaskParamRef = useRef(taskParam);
  useEffect(() => {
    if (taskParam === lastTaskParamRef.current) return;
    lastTaskParamRef.current = taskParam;
    setOpenTaskId(taskParam);
  }, [taskParam]);
  useEffect(() => {
    // state -> URL (opening/closing the drawer in the UI).
    const current = searchParams.get('task');
    if (openTaskId === current) return;
    // Remember what we're about to write so the URL->state effect above
    // treats this as its own echo and doesn't bounce it back.
    lastTaskParamRef.current = openTaskId;
    const next = new URLSearchParams(searchParams);
    if (openTaskId) next.set('task', openTaskId);
    else next.delete('task');
    setSearchParams(next, { replace: true });
  }, [openTaskId, searchParams, setSearchParams]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });

  // ---------- early returns ----------
  if (projectQ.isLoading || profilesQ.isLoading) {
    return <ProjectDetailSkeleton />;
  }
  if (projectQ.isError || !projectQ.data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Project not found"
        />
        <Button asChild variant="outline">
          <Link to="/tasks">
            <ChevronLeft className="h-4 w-4" />
            Back to My tasks
          </Link>
        </Button>
      </div>
    );
  }

  const project = projectQ.data;
  const department = departmentMeta(project.department);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Tasks"
        title={project.name}
        meta={
          <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <Link
              to="/tasks"
              className="inline-flex min-h-8 shrink-0 items-center gap-1 hover:underline"
            >
              <ChevronLeft className="h-3.5 w-3.5 sm:hidden" />
              My tasks
            </Link>
            {department && (
              <>
                <span className="hidden sm:inline">/</span>
                <Link
                  to={`/tasks?department=${department.slug}`}
                  className="hidden shrink-0 hover:underline sm:inline"
                >
                  {department.label}
                </Link>
              </>
            )}
            <span className="hidden sm:inline">/</span>
            <span className="hidden truncate text-foreground sm:inline">
              {project.name}
            </span>
            {department && (
              <Badge
                className={cn(
                  'ml-1 shrink-0 font-normal sm:hidden',
                  department.chipClassName,
                )}
              >
                {department.label}
              </Badge>
            )}
            {isArchived && (
              <Badge variant="muted" className="ml-1 shrink-0">
                Archived
              </Badge>
            )}
          </span>
        }
        actions={
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Button
              variant="outline"
              className="h-11 flex-1 sm:h-10 sm:flex-none"
              onClick={() => setShareOpen(true)}
            >
              <UserPlus className="h-4 w-4" />
              Share
            </Button>
            {(canArchive || canDelete || canEdit) && (
              <ProjectActionsMenu
                projectName={project.name}
                isMobile={isMobile}
                isArchived={isArchived}
                canArchive={canArchive}
                canDelete={canDelete}
                canEdit={canEdit}
                onClose={() => setCloseOpen(true)}
                onReopen={() => setCloseOpen(true)}
                onDelete={() => setDeleteOpen(true)}
                onEdit={() => setEditOpen(true)}
              />
            )}
          </div>
        }
      />

      <ProjectOverviewStrip
        project={project}
        stats={taskStats}
        members={memberProfiles}
        onOpenShare={() => setShareOpen(true)}
      />

      {isMobile ? (
        <div className="space-y-4 pb-24">
          <MobileTasksToolbar
            members={memberProfiles}
            filters={filters}
            onFiltersChange={setFilters}
            sort={sort}
            onSortChange={setSort}
            view={view}
            onViewChange={changeView}
          />
          {view === 'calendar' ? (
            <ProjectTaskCalendar
              projectId={projectId}
              tasks={calendarTasks}
              disabled={isArchived}
              isLoading={tasksQ.isLoading || sectionsQ.isLoading}
              onOpenTask={(taskId) => setOpenTaskId(taskId)}
            />
          ) : (
            <MobileTaskList
              projectId={projectId}
              buckets={grouped.buckets}
              linkedTasks={grouped.linkedTasks}
              subtasksByParent={grouped.subtasksByParent}
              visibleSubtasksByParent={grouped.visibleSubtasksByParent}
              expandedSubtasks={expandedSubtasks}
              onToggleSubtasks={(parentId) => {
                setExpandedSubtasks((prev) => {
                  const next = new Set(prev);
                  if (next.has(parentId)) next.delete(parentId);
                  else next.add(parentId);
                  return next;
                });
              }}
              collapsedSections={collapsedSections}
              onToggleSection={(sectionId) => {
                setCollapsedSections((prev) => {
                  const next = new Set(prev);
                  if (next.has(sectionId)) next.delete(sectionId);
                  else next.add(sectionId);
                  return next;
                });
              }}
              sectionsList={sectionsQ.data ?? []}
              allTasks={tasksQ.data ?? []}
              members={memberProfiles}
              otherProjects={otherProjects}
              disabled={isArchived}
              isLoading={tasksQ.isLoading || sectionsQ.isLoading}
              baseTotal={grouped.baseTotal}
              visibleTotal={grouped.visibleTotal}
              onOpenTask={(t) => setOpenTaskId(t.id)}
              onMoveSection={moveSection}
              onDeleteSection={setSectionToDelete}
              moves={{
                onMoveToSection: moveTaskToSection,
                onMakeSubtaskOf: makeTaskSubtaskOf,
                onPromoteToMainTask: promoteSubtaskToMain,
                onMoveToProject: moveTaskToProject,
                onAddToProject: addTaskToProject,
                onRemoveFromProject: removeTaskFromProject,
              }}
            />
          )}
          {grouped.baseTotal > 0 && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {grouped.visibleTotal} of {grouped.baseTotal} task
                {grouped.baseTotal === 1 ? '' : 's'} shown
              </span>
              {(filterCount > 0 || !filters.hideCompleted) && (
                <Button
                  variant="ghost"
                  className="h-9"
                  onClick={() => setFilters(freshEmptyFilter())}
                >
                  Reset filters
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Toolbar
            members={memberProfiles}
            filters={filters}
            onFiltersChange={setFilters}
            filterCount={filterCount}
            sort={sort}
            onClearSort={() => setSort(null)}
            view={view}
            onViewChange={changeView}
            wrapTaskName={wrapTaskName}
            onWrapTaskNameChange={changeWrapTaskName}
          />

          {view === 'calendar' && (
            <ProjectTaskCalendar
              projectId={projectId}
              tasks={calendarTasks}
              disabled={isArchived}
              isLoading={tasksQ.isLoading || sectionsQ.isLoading}
              onOpenTask={(taskId) => setOpenTaskId(taskId)}
            />
          )}

          {view === 'list' && (
          <WrapTaskNameContext.Provider value={wrapTaskName}>
          <DragDropContext
            onBeforeCapture={onBeforeCapture}
            onDragEnd={onDragEnd}
          >
            <div className="rounded-lg border border-border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14" />
                    <SortableHead
                      label="Task"
                      sortKey="name"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-full min-w-[200px]"
                    />
                    <SortableHead
                      label="Assignee"
                      sortKey="assignee"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[180px] min-w-[180px]"
                    />
                    <SortableHead
                      label="Due date"
                      sortKey="due_date"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[148px] min-w-[148px]"
                    />
                    <SortableHead
                      label="Workload"
                      sortKey="approximate_workload_hours"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[88px] min-w-[88px]"
                    />
                    <SortableHead
                      label="Priority"
                      sortKey="priority"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[150px] min-w-[150px]"
                    />
                    <SortableHead
                      label="Status"
                      sortKey="status"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[150px] min-w-[150px]"
                    />
                    <TableHead className="w-10" />
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                {tasksQ.isLoading || sectionsQ.isLoading ? (
                  <tbody>
                    <TableRow>
                      <TableCell
                        colSpan={TASK_COL_COUNT}
                        className="py-12 text-center text-muted-foreground"
                      >
                        Loading tasks…
                      </TableCell>
                    </TableRow>
                  </tbody>
                ) : (
                  <>
                    {/*
                      Friendly hint for a brand-new project. We render this
                      ABOVE the buckets (instead of replacing them) so the
                      inline "+ Add task" row in the unsectioned bucket
                      stays visible — that's the affordance the description
                      points the user at. Drops out the moment they have any
                      task OR any section.
                    */}
                    {grouped.baseTotal === 0 &&
                      (sectionsQ.data ?? []).length === 0 && (
                        <tbody>
                          <TableRow>
                            <TableCell colSpan={TASK_COL_COUNT} className="py-8 text-center">
                              <EmptyState
                                icon={ListTodo}
                                title="No tasks yet"
                              />
                            </TableCell>
                          </TableRow>
                        </tbody>
                      )}

                    {grouped.buckets.map((bucket) => {
                      const sectionId = bucket.section?.id ?? null;
                      const isCollapsed =
                        !!sectionId && collapsedSections.has(sectionId);
                      // The unsectioned bucket always renders (even when it
                      // has no tasks) so the inline "+ Add task" footer is
                      // available for top-level tasks. Without this the user
                      // would lose the ability to add an unsectioned task the
                      // moment they created their first section.
                      const sectionsList = sectionsQ.data ?? [];
                      const sectionIdx = bucket.section
                        ? sectionsList.findIndex(
                            (s) => s.id === bucket.section!.id,
                          )
                        : -1;
                      const canMoveSectionUp = sectionIdx > 0;
                      const canMoveSectionDown =
                        sectionIdx >= 0 &&
                        sectionIdx < sectionsList.length - 1;
                      return (
                        <SectionGroup
                          key={bucket.key}
                          section={bucket.section}
                          tasks={bucket.tasks}
                          linkedTasks={
                            bucket.section ? [] : grouped.linkedTasks
                          }
                          totalTaskCount={bucket.totalTaskCount}
                          members={memberProfiles}
                          disabled={isArchived}
                          projectId={projectId}
                          collapsed={isCollapsed}
                          onToggleCollapse={() => {
                            if (!sectionId) return;
                            setCollapsedSections((prev) => {
                              const next = new Set(prev);
                              if (next.has(sectionId)) next.delete(sectionId);
                              else next.add(sectionId);
                              return next;
                            });
                          }}
                          onDeleteSection={() => {
                            if (bucket.section)
                              setSectionToDelete(bucket.section);
                          }}
                          onOpenTask={(t) => setOpenTaskId(t.id)}
                          subtasksByParent={grouped.subtasksByParent}
                          visibleSubtasksByParent={
                            grouped.visibleSubtasksByParent
                          }
                          expandedSubtasks={expandedSubtasks}
                          onToggleSubtasks={(parentId) => {
                            setExpandedSubtasks((prev) => {
                              const next = new Set(prev);
                              if (next.has(parentId)) next.delete(parentId);
                              else next.add(parentId);
                              return next;
                            });
                          }}
                          sectionsList={sectionsList}
                          allTasks={tasksQ.data ?? []}
                          canMoveSectionUp={canMoveSectionUp}
                          canMoveSectionDown={canMoveSectionDown}
                          onMoveSectionUp={() => {
                            if (bucket.section)
                              moveSection(bucket.section.id, 'up');
                          }}
                          onMoveSectionDown={() => {
                            if (bucket.section)
                              moveSection(bucket.section.id, 'down');
                          }}
                          onMoveTaskToSection={moveTaskToSection}
                          onMakeTaskSubtaskOf={makeTaskSubtaskOf}
                          onPromoteSubtaskToMain={promoteSubtaskToMain}
                          otherProjects={otherProjects}
                          onMoveTaskToProject={moveTaskToProject}
                          onAddTaskToProject={addTaskToProject}
                          onRemoveTaskFromProject={removeTaskFromProject}
                          hideComposers={isTaskDragActive}
                        />
                      );
                    })}
                  </>
                )}
                {grouped.visibleTotal === 0 && grouped.baseTotal > 0 && (
                  <tbody>
                    <TableRow>
                      <TableCell colSpan={TASK_COL_COUNT} className="py-6 text-center">
                        <span className="text-sm text-muted-foreground">
                          No tasks match the current filters.
                        </span>
                      </TableCell>
                    </TableRow>
                  </tbody>
                )}
                <tbody>
                  <AddSectionRow projectId={projectId} disabled={isArchived} />
                </tbody>
              </Table>
            </div>
          </DragDropContext>
          </WrapTaskNameContext.Provider>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {grouped.visibleTotal} of {grouped.baseTotal} task(s) shown
            </span>
            {filterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters(freshEmptyFilter())}
              >
                Clear filters
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        projectId={projectId}
        creatorId={project.created_by}
        members={memberProfiles}
        canManage={!!canManageMembers}
        allProfiles={(profilesQ.data ?? [])
          .filter((p) => p.is_active)
          .map((p) => ({
            id: p.id,
            full_name: p.full_name,
            email: p.email,
            avatar_url: p.avatar_url,
          }))}
      />

      <CloseProjectDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        projectId={projectId}
        projectName={project.name}
        isArchived={isArchived}
      />

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectId={projectId}
        projectName={project.name}
        onDeleted={() => navigate('/tasks')}
      />

      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        projectId={projectId}
        currentName={project.name}
        currentDepartment={project.department}
      />

      <TaskDetailDrawer
        open={!!openTaskId}
        task={openTask}
        members={memberProfiles}
        disabled={isArchived}
        onOpenChange={(next) => {
          if (!next) setOpenTaskId(null);
        }}
        onOpenTask={(id) => setOpenTaskId(id)}
      />

      <DeleteSectionDialog
        section={sectionToDelete}
        projectId={projectId}
        onOpenChange={(open) => {
          if (!open) setSectionToDelete(null);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project actions menu — edit / close / reopen / delete
// ---------------------------------------------------------------------------

function ProjectActionsMenu({
  projectName,
  isMobile,
  isArchived,
  canArchive,
  canDelete,
  canEdit,
  onClose,
  onReopen,
  onDelete,
  onEdit,
}: {
  projectName: string;
  isMobile: boolean;
  isArchived: boolean;
  canArchive: boolean;
  canDelete: boolean;
  canEdit: boolean;
  onClose: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  if (isMobile) {
    const run = (fn: () => void) => () => {
      setSheetOpen(false);
      fn();
    };
    return (
      <>
        <Button
          variant="outline"
          size="icon"
          className="h-11 w-11"
          aria-label="Project actions"
          onClick={() => setSheetOpen(true)}
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>
        <ActionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={projectName}
          description="Project"
        >
          {canEdit && (
            <ActionSheetItem
              icon={Pencil}
              label="Edit project"
              onSelect={run(onEdit)}
            />
          )}
          {canArchive &&
            (isArchived ? (
              <ActionSheetItem
                icon={ArchiveRestore}
                label="Reopen project"
                onSelect={run(onReopen)}
              />
            ) : (
              <ActionSheetItem
                icon={Archive}
                label="Close project"
                onSelect={run(onClose)}
              />
            ))}
          {canDelete && (
            <ActionSheetItem
              icon={Trash2}
              label="Delete project…"
              destructive
              onSelect={run(onDelete)}
            />
          )}
        </ActionSheet>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Project actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Project</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canEdit && (
          <DropdownMenuItem onSelect={() => onEdit()}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit project
          </DropdownMenuItem>
        )}
        {canArchive &&
          (isArchived ? (
            <DropdownMenuItem onSelect={() => onReopen()}>
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Reopen project
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => onClose()}>
              <Archive className="mr-2 h-4 w-4" />
              Close project
            </DropdownMenuItem>
          ))}
        {canDelete && (
          <>
            {(canArchive || canEdit) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={() => onDelete()}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete project…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Close / reopen confirmation
//
// Closing is a soft archive — keeps tasks + audit history intact and
// just removes the project from the sidebar / active dashboard list.
// Reopening flips archived_at back to null. Either operation runs
// through the same dialog so the action button text + description
// adapt to the current state.
// ---------------------------------------------------------------------------

function CloseProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  isArchived,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  isArchived: boolean;
}) {
  const archive = useArchiveProject();

  const onConfirm = async () => {
    try {
      await archive.mutateAsync({ id: projectId, archived: !isArchived });
      toast.success(
        isArchived
          ? `Reopened “${projectName}”`
          : `Closed “${projectName}”. You can reopen it from the dashboard.`,
      );
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isArchived ? 'Reopen project?' : 'Close project?'}
          </DialogTitle>
          <DialogDescription>
            {isArchived
              ? `“${projectName}” will return to the active list. All tasks stay as they were.`
              : `“${projectName}” will be archived and removed from the sidebar. Tasks and history are kept.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={archive.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={archive.isPending}>
            {archive.isPending
              ? 'Saving…'
              : isArchived
              ? 'Reopen project'
              : 'Close project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation
//
// Hard-delete is irreversible (the FK cascade wipes tasks +
// memberships), so we gate the confirm button behind an exact-match
// type-the-name affordance. This is the same pattern GitHub /
// Linear / Vercel use for destructive ops.
// ---------------------------------------------------------------------------

function DeleteProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  onDeleted: () => void;
}) {
  const deleteProject = useDeleteProject();
  const [confirmText, setConfirmText] = useState('');

  // Reset the typed confirmation whenever the dialog re-opens so a
  // previously typed value can't survive a Cancel + reopen.
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const matches = confirmText.trim() === projectName;

  const onConfirm = async () => {
    if (!matches) return;
    try {
      await deleteProject.mutateAsync({ id: projectId });
      toast.success(`Deleted “${projectName}”`);
      onOpenChange(false);
      onDeleted();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete project?</DialogTitle>
          <DialogDescription>
            This permanently removes{' '}
            <span className="font-medium text-foreground">“{projectName}”</span>{' '}
            and every task on it. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label
            htmlFor="delete-project-confirm"
            className="text-xs text-muted-foreground"
          >
            Type <span className="font-mono text-foreground">{projectName}</span>{' '}
            to confirm.
          </label>
          <Input
            id="delete-project-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={projectName}
            autoComplete="off"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteProject.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!matches || deleteProject.isPending}
          >
            {deleteProject.isPending ? 'Deleting…' : 'Delete project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit project dialog — name + owning department
// ---------------------------------------------------------------------------

// Sentinel for "no department" in <Select>, which can't represent an
// empty-string value.
const NO_DEPARTMENT = '__none__';

function EditProjectDialog({
  open,
  onOpenChange,
  projectId,
  currentName,
  currentDepartment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentName: string;
  currentDepartment: string | null;
}) {
  const updateProject = useUpdateProject();
  const [name, setName] = useState(currentName);
  const [department, setDepartment] = useState<string>(
    currentDepartment ?? NO_DEPARTMENT,
  );

  useEffect(() => {
    if (open) {
      setName(currentName);
      setDepartment(currentDepartment ?? NO_DEPARTMENT);
    }
  }, [open, currentName, currentDepartment]);

  const trimmed = name.trim();
  const nextDepartment = department === NO_DEPARTMENT ? null : department;
  const dirty =
    (trimmed !== '' && trimmed !== currentName) ||
    nextDepartment !== (currentDepartment ?? null);

  const onConfirm = async () => {
    if (!trimmed || !dirty) {
      onOpenChange(false);
      return;
    }
    try {
      await updateProject.mutateAsync({
        id: projectId,
        patch: { name: trimmed, department: nextDepartment },
      });
      toast.success('Project updated');
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      toast.error(msg);
    }
  };

  // A stored department that has since been removed from DEPARTMENTS
  // still needs a row so the current value is visible and re-selectable.
  const legacyDepartment =
    currentDepartment && !isDepartmentSlug(currentDepartment)
      ? departmentMeta(currentDepartment)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Block only focus-outside dismissal so switching browser tabs
        // (which fires focus-outside) doesn't discard an in-progress
        // edit. The X, Escape, and outside clicks still close it.
        onFocusOutside={(e) => e.preventDefault()}
        className="max-h-[90dvh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-project-name">Project name</Label>
            <Input
              id="edit-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q3 Roadmap"
              autoComplete="off"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onConfirm();
                }
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-project-department">Department</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger id="edit-project-department" className="h-11 w-full sm:h-10">
                {nextDepartment ? (
                  <span>{departmentMeta(nextDepartment)?.label}</span>
                ) : (
                  <span className="text-muted-foreground">No department</span>
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEPARTMENT}>No department</SelectItem>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d.slug} value={d.slug}>
                    {d.label}
                  </SelectItem>
                ))}
                {legacyDepartment && (
                  <SelectItem value={legacyDepartment.slug}>
                    {legacyDepartment.label}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Groups this list under the department that owns it. Tasks
              still show up for everyone assigned, whatever the department.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateProject.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!trimmed || !dirty || updateProject.isPending}
          >
            {updateProject.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Member chip rail
// ---------------------------------------------------------------------------

interface MemberLite {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
}

// ---------------------------------------------------------------------------
// Overview strip — progress, task health and members at a glance
// ---------------------------------------------------------------------------

const OVERVIEW_TONE = {
  info: 'bg-info-soft/30 text-info',
  warning: 'bg-fresh-salmon/25 text-fresh-salmon-dark',
  success: 'bg-bright-moss/35 text-bright-moss-dark',
  neutral: 'bg-secondary text-muted-foreground',
} as const;

function ProjectOverviewStrip({
  project,
  stats,
  members,
  onOpenShare,
}: {
  project: ProjectWithMembers;
  stats: { total: number; done: number; open: number; overdue: number };
  members: MemberLite[];
  onOpenShare: () => void;
}) {
  const pct = completionPercent(stats.total, stats.open);
  const isArchived = !!project.archived_at;
  // Overdue work is what should read as a warning here — the task
  // list has no deadline of its own.
  const behind = !isArchived && stats.overdue > 0;

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:gap-6">
        <div className="flex min-w-0 items-center gap-3 lg:w-80 lg:shrink-0">
          <ProjectMonogram
            id={project.id}
            name={project.name}
            size="lg"
            muted={isArchived}
          />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-muted-foreground">
                <span className="text-2xl font-light tracking-headline tabular-nums text-foreground">
                  {pct}%
                </span>{' '}
                complete
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {stats.done} of {stats.total}{' '}
                {stats.total === 1 ? 'task' : 'tasks'}
              </span>
            </div>
            <CompletionBar
              value={pct}
              tone={behind ? 'warning' : 'default'}
              label="Project completion"
            />
          </div>
        </div>

        <dl className="grid grid-cols-3 gap-2 lg:flex-1">
          <OverviewStat label="Open" value={stats.open} tone="info" />
          <OverviewStat
            label="Overdue"
            value={stats.overdue}
            tone={stats.overdue > 0 ? 'warning' : 'neutral'}
          />
          <OverviewStat label="Done" value={stats.done} tone="success" />
        </dl>

        <div className="flex items-center justify-between gap-3 border-t border-border pt-3 lg:border-0 lg:pt-0">
          <div className="flex min-w-0 items-center gap-2">
            <AvatarStack people={members} max={5} size="sm" />
            <span className="truncate text-xs text-muted-foreground">
              {members.length === 0
                ? 'No one shared yet'
                : `${members.length} member${members.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <Button
            variant="ghost"
            className="h-10 shrink-0 px-3 text-info"
            onClick={onOpenShare}
          >
            <UserPlus className="h-4 w-4" />
            Manage
          </Button>
        </div>
      </div>
    </Card>
  );
}

function OverviewStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: keyof typeof OVERVIEW_TONE;
}) {
  return (
    <div className={cn('rounded-lg px-3 py-2', OVERVIEW_TONE[tone])}>
      <dt className="text-[11px] font-medium uppercase tracking-wider opacity-80">
        {label}
      </dt>
      <dd className="text-lg font-medium leading-tight tabular-nums">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile toolbar — one "Filter & sort" entry point plus the view toggle.
// Sticks to the top of the scroll container so filters stay reachable
// on long lists.
// ---------------------------------------------------------------------------

const MOBILE_SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'due_date', label: 'Due date' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'name', label: 'Task name' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'approximate_workload_hours', label: 'Workload' },
];

function MobileTasksToolbar({
  members,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  view,
  onViewChange,
}: {
  members: MemberLite[];
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  sort: { key: SortKey; dir: SortDir } | null;
  onSortChange: (next: { key: SortKey; dir: SortDir } | null) => void;
  view: ViewMode;
  onViewChange: (next: ViewMode) => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const toggleIn = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  const memberName = (id: string) => {
    if (id === '__none__') return 'Unassigned';
    const m = members.find((x) => x.id === id);
    return m ? (m.full_name ?? m.email) : 'Unknown';
  };

  const activeChips: Array<{ key: string; label: string; onRemove: () => void }> = [
    ...Array.from(filters.assignees).map((id) => ({
      key: `a-${id}`,
      label: memberName(id),
      onRemove: () =>
        onFiltersChange({ ...filters, assignees: toggleIn(filters.assignees, id) }),
    })),
    ...Array.from(filters.priorities).map((p) => ({
      key: `p-${p}`,
      label: PRIORITY_META[p].label,
      onRemove: () =>
        onFiltersChange({ ...filters, priorities: toggleIn(filters.priorities, p) }),
    })),
    ...Array.from(filters.statuses).map((s) => ({
      key: `s-${s}`,
      label: STATUS_META[s].label,
      onRemove: () =>
        onFiltersChange({ ...filters, statuses: toggleIn(filters.statuses, s) }),
    })),
    ...(!filters.hideCompleted
      ? [
          {
            key: 'completed',
            label: 'Showing completed',
            onRemove: () => onFiltersChange({ ...filters, hideCompleted: true }),
          },
        ]
      : []),
    ...(sort && view === 'list'
      ? [
          {
            key: 'sort',
            label: `Sort: ${labelForSortKey(sort.key)} ${sort.dir === 'asc' ? '↑' : '↓'}`,
            onRemove: () => onSortChange(null),
          },
        ]
      : []),
  ];
  const activeCount =
    filters.assignees.size +
    filters.priorities.size +
    filters.statuses.size +
    (filters.hideCompleted ? 0 : 1) +
    (sort ? 1 : 0);

  const viewButton = (mode: ViewMode, Icon: typeof List, label: string) => (
    <button
      type="button"
      onClick={() => onViewChange(mode)}
      aria-label={`${label} view`}
      aria-pressed={view === mode}
      className={cn(
        'flex h-full w-11 items-center justify-center rounded-md transition-colors',
        view === mode
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground active:bg-secondary',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="sticky top-0 z-20 -mx-4 space-y-2 border-b border-border bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="h-11 flex-1 justify-start bg-card"
          onClick={() => setSheetOpen(true)}
        >
          <ListFilter className="h-4 w-4" />
          Filter &amp; sort
          {activeCount > 0 && (
            <Badge variant="info" className="ml-auto px-2 py-0 tabular-nums">
              {activeCount}
            </Badge>
          )}
        </Button>
        <div className="flex h-11 items-center rounded-xl border border-input bg-card p-0.5">
          {viewButton('list', List, 'List')}
          {viewButton('calendar', CalendarDays, 'Calendar')}
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
              className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full border border-info-soft/60 bg-info-soft/30 pl-3 pr-2 text-xs text-info"
            >
              {chip.label}
              <X className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}

      <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <BottomSheetHeader
          title="Filter & sort"
        />
        <BottomSheetBody className="space-y-6">
          <label className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-border bg-card px-3">
            <span className="text-sm text-foreground">Show completed tasks</span>
            <Switch
              checked={!filters.hideCompleted}
              onCheckedChange={(v) =>
                onFiltersChange({ ...filters, hideCompleted: !v })
              }
            />
          </label>

          {view === 'list' && (
            <SheetSection
              title="Sort"
              action={
                sort && (
                  <button
                    type="button"
                    onClick={() => onSortChange(null)}
                    className="min-h-8 text-xs text-info"
                  >
                    Default order
                  </button>
                )
              }
            >
              <div className="grid grid-cols-2 gap-1.5">
                {MOBILE_SORT_OPTIONS.map((opt) => {
                  const active = sort?.key === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() =>
                        onSortChange(
                          active
                            ? { key: opt.key, dir: sort!.dir === 'asc' ? 'desc' : 'asc' }
                            : { key: opt.key, dir: 'asc' },
                        )
                      }
                      aria-pressed={active}
                      className={cn(
                        'flex min-h-11 items-center justify-between gap-2 rounded-lg border px-3 text-left text-sm transition-colors',
                        active
                          ? 'border-info bg-info-soft/25 text-info'
                          : 'border-border bg-card text-foreground active:bg-secondary',
                      )}
                    >
                      {opt.label}
                      {active ? (
                        sort?.dir === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-muted-foreground/40" />
                      )}
                    </button>
                  );
                })}
              </div>
            </SheetSection>
          )}

          <SheetSection title="Assignee">
            <div className="flex flex-wrap gap-1.5">
              {[{ id: '__none__', label: 'Unassigned' }, ...members.map((m) => ({ id: m.id, label: m.full_name ?? m.email }))].map(
                (opt) => (
                  <SheetChip
                    key={opt.id}
                    active={filters.assignees.has(opt.id)}
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        assignees: toggleIn(filters.assignees, opt.id),
                      })
                    }
                  >
                    {opt.label}
                  </SheetChip>
                ),
              )}
            </div>
          </SheetSection>

          <SheetSection title="Priority">
            <div className="flex flex-wrap gap-1.5">
              {PRIORITY_ORDER.map((p) => {
                const active = filters.priorities.has(p);
                return (
                  <SheetChip
                    key={p}
                    active={active}
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        priorities: toggleIn(filters.priorities, p),
                      })
                    }
                  >
                    {!active && (
                      <span
                        aria-hidden
                        className={cn('h-2 w-2 rounded-full', PRIORITY_META[p].dot)}
                      />
                    )}
                    {PRIORITY_META[p].label}
                  </SheetChip>
                );
              })}
            </div>
          </SheetSection>

          <SheetSection title="Status">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_ORDER.map((s) => {
                const active = filters.statuses.has(s);
                return (
                  <SheetChip
                    key={s}
                    active={active}
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        statuses: toggleIn(filters.statuses, s),
                      })
                    }
                  >
                    {!active && (
                      <span
                        aria-hidden
                        className={cn('h-2 w-2 rounded-full', STATUS_META[s].dot)}
                      />
                    )}
                    {STATUS_META[s].label}
                  </SheetChip>
                );
              })}
            </div>
          </SheetSection>
        </BottomSheetBody>
        <BottomSheetFooter>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              className="h-11 w-full sm:w-auto"
              onClick={() => {
                onFiltersChange(freshEmptyFilter());
                onSortChange(null);
              }}
            >
              Reset
            </Button>
          )}
          <Button
            className="h-11 w-full sm:w-auto"
            onClick={() => setSheetOpen(false)}
          >
            Done
          </Button>
        </BottomSheetFooter>
      </BottomSheet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar — filters + sort indicator
// ---------------------------------------------------------------------------

function Toolbar({
  members,
  filters,
  onFiltersChange,
  filterCount,
  sort,
  onClearSort,
  view,
  onViewChange,
  wrapTaskName,
  onWrapTaskNameChange,
}: {
  members: MemberLite[];
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  filterCount: number;
  sort: { key: SortKey; dir: SortDir } | null;
  onClearSort: () => void;
  view: ViewMode;
  onViewChange: (next: ViewMode) => void;
  wrapTaskName: boolean;
  onWrapTaskNameChange: (next: boolean) => void;
}) {
  const toggleAssignee = (id: string) => {
    const next = new Set(filters.assignees);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onFiltersChange({ ...filters, assignees: next });
  };
  const togglePriority = (p: TaskPriority) => {
    const next = new Set(filters.priorities);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    onFiltersChange({ ...filters, priorities: next });
  };
  const toggleStatus = (s: TaskStatus) => {
    const next = new Set(filters.statuses);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    onFiltersChange({ ...filters, statuses: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterDropdown
        title="Assignee"
        count={filters.assignees.size}
        options={[
          { value: '__none__', label: 'Unassigned' },
          ...members.map((m) => ({
            value: m.id,
            label: m.full_name ?? m.email,
          })),
        ]}
        selected={filters.assignees}
        onToggle={(v) => toggleAssignee(v)}
        onClear={() => onFiltersChange({ ...filters, assignees: new Set() })}
      />

      <FilterDropdown
        title="Priority"
        count={filters.priorities.size}
        options={PRIORITY_ORDER.map((p) => ({
          value: p,
          label: PRIORITY_META[p].label,
        }))}
        selected={filters.priorities as Set<string>}
        onToggle={(v) => togglePriority(v as TaskPriority)}
        onClear={() => onFiltersChange({ ...filters, priorities: new Set() })}
      />

      <FilterDropdown
        title="Status"
        count={filters.statuses.size}
        options={STATUS_ORDER.map((s) => ({
          value: s,
          label: STATUS_META[s].label,
        }))}
        selected={filters.statuses as Set<string>}
        onToggle={(v) => toggleStatus(v as TaskStatus)}
        onClear={() => onFiltersChange({ ...filters, statuses: new Set() })}
      />

      <Button
        variant={filters.hideCompleted ? 'secondary' : 'outline'}
        size="sm"
        onClick={() =>
          onFiltersChange({ ...filters, hideCompleted: !filters.hideCompleted })
        }
      >
        {filters.hideCompleted ? 'Show completed' : 'Hide completed'}
      </Button>

      <div className="flex-1" />

      {sort && view === 'list' && (
        <Button variant="ghost" size="sm" onClick={onClearSort}>
          Sorted by {labelForSortKey(sort.key)} ({sort.dir})
          <X className="h-3.5 w-3.5" />
        </Button>
      )}

      {filterCount > 0 && (
        <Badge variant="default" className="rounded-full">
          {filterCount} filter{filterCount === 1 ? '' : 's'}
        </Badge>
      )}

      {view === 'list' && (
        <Button
          variant={wrapTaskName ? 'default' : 'outline'}
          size="sm"
          onClick={() => onWrapTaskNameChange(!wrapTaskName)}
          aria-pressed={wrapTaskName}
          title={
            wrapTaskName
              ? 'Wrap text is on — click to truncate long task names to one line'
              : 'Wrap text is off — click to let long task names wrap'
          }
        >
          <WrapText className="h-3.5 w-3.5" />
          {wrapTaskName ? 'Wrap: on' : 'Wrap: off'}
        </Button>
      )}

      {/* List / Calendar toggle — same control as the social planner. */}
      <div className="flex items-center rounded-lg border border-input bg-card p-0.5">
        <button
          type="button"
          onClick={() => onViewChange('list')}
          aria-label="List view"
          className={cn(
            'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
            view === 'list'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <List className="h-3.5 w-3.5" />
          List
        </button>
        <button
          type="button"
          onClick={() => onViewChange('calendar')}
          aria-label="Calendar view"
          className={cn(
            'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
            view === 'calendar'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Calendar
        </button>
      </div>
    </div>
  );
}

function FilterDropdown({
  title,
  count,
  options,
  selected,
  onToggle,
  onClear,
}: {
  title: string;
  count: number;
  options: Array<{ value: string; label: string }>;
  selected: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          <ListFilter className="h-3.5 w-3.5" />
          {title}
          {count > 0 && (
            <>
              <span className="mx-1 h-3.5 w-px bg-border" />
              <Badge variant="default" className="rounded px-1.5 py-0">
                {count}
              </Badge>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            No options.
          </div>
        ) : (
          options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={selected.has(o.value)}
              onSelect={(e) => {
                e.preventDefault();
                onToggle(o.value);
              }}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          ))
        )}
        {count > 0 && (
          <>
            <DropdownMenuSeparator />
            <button
              type="button"
              onClick={onClear}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:bg-secondary/60"
            >
              Clear {title.toLowerCase()}
            </button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir } | null;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-secondary/60"
      >
        {label}
        {!active && <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
        {active && sort?.dir === 'asc' && <ArrowUp className="h-3 w-3" />}
        {active && sort?.dir === 'desc' && <ArrowDown className="h-3 w-3" />}
      </button>
    </TableHead>
  );
}

function labelForSortKey(key: SortKey): string {
  switch (key) {
    case 'name':
      return 'Task';
    case 'assignee':
      return 'Assignee';
    case 'due_date':
      return 'Due date';
    case 'approximate_workload_hours':
      return 'Workload';
    case 'priority':
      return 'Priority';
    case 'status':
      return 'Status';
    case 'is_completed':
      return 'Completion';
  }
}

// ---------------------------------------------------------------------------
// Drag-and-drop helpers
//
// Droppable ids encode which "bucket" they represent so the onDragEnd
// handler can route a drop to the right reorder math without having
// to look the section up by index. We use a synthetic '__none__' for
// the unsectioned bucket so the URL-safe id stays predictable and
// the page-level lookups in `parentsInBucket` only ever have to deal
// with `string | null`.
// ---------------------------------------------------------------------------

const BUCKET_DROPPABLE_PREFIX = 'bucket-';
const UNSECTIONED_DROPPABLE_KEY = '__none__';
const SECTION_DROPPABLE_PREFIX = 'section-';

function bucketDroppableId(sectionId: string | null): string {
  return `${BUCKET_DROPPABLE_PREFIX}${sectionId ?? UNSECTIONED_DROPPABLE_KEY}`;
}

function isBucketDroppableId(droppableId: string): boolean {
  return droppableId.startsWith(BUCKET_DROPPABLE_PREFIX);
}

// Returns the section id for a bucket droppable, or `null` for the
// unsectioned bucket. Caller must have already verified this is a
// bucket droppable via `isBucketDroppableId` — for non-bucket
// droppable ids the return is also `null`, which is otherwise
// indistinguishable from the unsectioned bucket.
function bucketIdFromDroppableId(droppableId: string): string | null {
  if (!droppableId.startsWith(BUCKET_DROPPABLE_PREFIX)) return null;
  const tail = droppableId.slice(BUCKET_DROPPABLE_PREFIX.length);
  return tail === UNSECTIONED_DROPPABLE_KEY ? null : tail;
}

function sectionDroppableId(sectionId: string): string {
  return `${SECTION_DROPPABLE_PREFIX}${sectionId}`;
}

function sectionDroppableIdToSectionId(droppableId: string): string | null {
  if (!droppableId.startsWith(SECTION_DROPPABLE_PREFIX)) return null;
  return droppableId.slice(SECTION_DROPPABLE_PREFIX.length);
}

/**
 * One draggable row in a section bucket's flattened list: either a
 * top-level task (`parentId: null`) or a subtask rendered beneath its
 * expanded parent (`parentId` set). Parents and subtasks share one
 * droppable per bucket so a drag can cross hierarchy levels — the
 * onDragEnd handler reads the drop slot's neighbours from this shape
 * to decide whether a drop re-parents, promotes, or just reorders.
 */
type FlatRowEntry = { task: TaskWithAssignees; parentId: string | null };

function flatBucketRows(
  parents: TaskWithAssignees[],
  visibleSubtasksByParent: Map<string, TaskWithAssignees[]>,
  expandedSubtasks: Set<string>,
): FlatRowEntry[] {
  const rows: FlatRowEntry[] = [];
  for (const p of parents) {
    rows.push({ task: p, parentId: null });
    if (expandedSubtasks.has(p.id)) {
      for (const c of visibleSubtasksByParent.get(p.id) ?? []) {
        rows.push({ task: c, parentId: p.id });
      }
    }
  }
  return rows;
}

function parentsInBucket(
  tasks: TaskWithAssignees[],
  sectionId: string | null,
): TaskWithAssignees[] {
  return tasks
    .filter(
      (t) => t.parent_task_id === null && (t.section_id ?? null) === sectionId,
    )
    .sort((a, b) => a.position - b.position);
}

/**
 * Strip moves that wouldn't change the cached row, so we don't ship a
 * no-op write to the RPC. Saves a round-trip for a same-spot drop
 * (which `@hello-pangea/dnd` will still call onDragEnd for if the
 * user releases over the original index) and keeps the audit log
 * cleaner.
 */
function filterChangedMoves(
  moves: TaskMove[],
  current: TaskWithAssignees[],
): TaskMove[] {
  const byId = new Map(current.map((t) => [t.id, t]));
  return moves.filter((m) => {
    const t = byId.get(m.id);
    if (!t) return true;
    return (
      t.position !== m.position ||
      (t.section_id ?? null) !== m.section_id ||
      (t.parent_task_id ?? null) !== m.parent_task_id
    );
  });
}

// ---------------------------------------------------------------------------
// Sort comparator
// ---------------------------------------------------------------------------

function compareTasks(a: TaskWithAssignees, b: TaskWithAssignees, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'assignee': {
      // Sort by the first assignee's display name. The hooks layer
      // already pre-sorts each task's assignees alphabetically, so
      // [0] is the lexicographically smallest one — stable across
      // refetches. Empty assignee lists sort to the end so an
      // ascending sort leads with the rows that actually have owners.
      const an = a.assignees[0]?.full_name ?? a.assignees[0]?.email ?? '';
      const bn = b.assignees[0]?.full_name ?? b.assignees[0]?.email ?? '';
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn);
    }
    case 'due_date': {
      // Nulls always sort to the end so a `due_date asc` view leads
      // with the most-imminent deadlines.
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    }
    case 'approximate_workload_hours': {
      const ah = a.approximate_workload_hours;
      const bh = b.approximate_workload_hours;
      if (ah == null && bh == null) return 0;
      if (ah == null) return 1;
      if (bh == null) return -1;
      return ah - bh;
    }
    case 'priority':
      return PRIORITY_META[a.priority].order - PRIORITY_META[b.priority].order;
    case 'status':
      return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
    case 'is_completed':
      return Number(a.is_completed) - Number(b.is_completed);
  }
}

// ---------------------------------------------------------------------------
// Task name field — single-line (truncated) or wrapping auto-grow
//
// HTML inputs cannot wrap, so wrap-on uses a textarea that grows with
// the content. Enter still commits (no newlines in task names). The
// wrap preference comes from WrapTaskNameContext so every composer and
// row picks it up without prop drilling through SectionGroup.
// ---------------------------------------------------------------------------

function TaskNameField({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  className,
  onEnter,
}: {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onEnter?: () => void;
}) {
  const wrap = useContext(WrapTaskNameContext);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (!wrap) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, wrap]);

  const commitOnEnter = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (onEnter) onEnter();
    else (e.target as HTMLElement).blur();
  };

  const chrome =
    'min-w-0 flex-1 border-transparent bg-transparent px-2 hover:border-border focus:border-input';

  if (!wrap) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={commitOnEnter}
        disabled={disabled}
        placeholder={placeholder}
        title={value || undefined}
        className={cn(chrome, className, 'h-8')}
      />
    );
  }

  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={commitOnEnter}
      disabled={disabled}
      placeholder={placeholder}
      className={cn(
        chrome,
        className,
        'min-h-8 h-auto resize-none overflow-hidden break-words py-1.5 leading-5',
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Editable task row
// ---------------------------------------------------------------------------

function TaskRow({
  task,
  members,
  disabled,
  onOpen,
  isSubtask = false,
  subtaskCount = 0,
  hasSubtasks = false,
  subtasksExpanded = false,
  onToggleSubtasks,
  currentProjectId,
  sectionsList,
  allTasks,
  onMoveToSection,
  onMakeSubtaskOf,
  onPromoteToMainTask,
  otherProjects,
  onMoveToProject,
  onAddToProject,
  onRemoveFromProject,
  draggableProvided,
  isDragging = false,
}: {
  task: TaskWithAssignees;
  members: MemberLite[];
  disabled: boolean;
  onOpen: () => void;
  /**
   * `true` when this row is rendered as a child under its parent.
   * Subtasks share the same column layout as top-level rows but get
   * an indent + a "↳" marker in the name cell and a slightly
   * subdued background so the grouping reads visually.
   */
  isSubtask?: boolean;
  /** Number of children this row has (parents only). */
  subtaskCount?: number;
  hasSubtasks?: boolean;
  subtasksExpanded?: boolean;
  onToggleSubtasks?: () => void;
  /**
   * The project whose page is rendering this row. When it differs
   * from `task.project_id` the row is a *linked* task multi-homed in
   * from another project — it gets a "from <project>" chip and a
   * reduced actions menu.
   */
  currentProjectId: string;
  /**
   * Project-wide context for the kebab move menu. `sectionsList`
   * powers the "Move to section…" submenu; `allTasks` is filtered
   * down to legal parents for the "Make subtask of…" submenu (any
   * top-level task other than this one). The callbacks are supplied
   * by the page so the position math lives in one place.
   */
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  onMoveToSection: (task: TaskWithAssignees, sectionId: string | null) => void;
  onMakeSubtaskOf: (task: TaskWithAssignees, parentId: string) => void;
  onPromoteToMainTask: (task: TaskWithAssignees) => void;
  otherProjects: ProjectOption[];
  onMoveToProject: (task: TaskWithAssignees, projectId: string) => void;
  onAddToProject: (task: TaskWithAssignees, projectId: string) => void;
  onRemoveFromProject: (task: TaskWithAssignees) => void;
  /**
   * `@hello-pangea/dnd` draggable provided object. When present the
   * row participates in drag-and-drop reorder: the grip icon in the
   * first cell becomes the drag handle, and the row itself wires the
   * library's ref + draggableProps. Passed `null` when the row is
   * being rendered outside a Draggable (currently never, but the
   * type accepts null so the component stays usable as a pure
   * display).
   */
  draggableProvided?: DraggableProvided | null;
  isDragging?: boolean;
}) {
  const wrap = useContext(WrapTaskNameContext);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const setAssignees = useSetTaskAssignees();

  const isLinked = task.project_id !== currentProjectId;

  // Local mirror of the name field so the input stays controlled and we
  // only persist on blur (avoiding a network call per keystroke).
  const [name, setName] = useState(task.name);
  useEffect(() => {
    setName(task.name);
  }, [task.name]);

  const persist = (patch: Parameters<typeof updateTask.mutate>[0]['patch']) => {
    updateTask.mutate(
      { id: task.id, projectId: task.project_id, patch },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Update failed';
          toast.error(msg);
        },
      },
    );
  };

  const onNameBlur = () => {
    const trimmed = name.trim();
    if (trimmed === task.name) return;
    if (!trimmed) {
      setName(task.name);
      toast.error('Task name cannot be empty.');
      return;
    }
    persist({ name: trimmed });
  };

  const onDelete = () => {
    const label = isSubtask ? 'subtask' : 'task';
    const tail = subtaskCount > 0
      ? ` This will also delete ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}.`
      : '';
    if (!window.confirm(`Delete ${label} “${task.name}”?${tail}`)) return;
    deleteTask.mutate(
      { id: task.id, projectId: task.project_id },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Delete failed';
          toast.error(msg);
        },
      },
    );
  };

  const onAssigneesChange = (nextIds: string[]) => {
    setAssignees.mutate(
      {
        taskId: task.id,
        projectId: task.project_id,
        profileIds: nextIds,
        currentProfileIds: task.assignees.map((a) => a.id),
      },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Update failed';
          toast.error(msg);
        },
      },
    );
  };

  // Stop click propagation at the cell level so the surrounding
  // padding around an editor doesn't bubble up and pop the drawer.
  // Only the chevron cell at the end (and naked row borders) act as
  // the explicit "open details" affordance.
  const stopRowOpen = (e: MouseEvent) => e.stopPropagation();

  return (
    <TableRow
      data-completed={task.is_completed}
      ref={draggableProvided?.innerRef}
      {...(draggableProvided?.draggableProps ?? {})}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${task.name}`}
      onKeyDown={(e) => {
        // Only intercept Enter / Space when focus is actually on the
        // row itself — pressing Enter inside the task-name input
        // should still blur+save it.
        if (
          (e.key === 'Enter' || e.key === ' ') &&
          e.target === e.currentTarget
        ) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'group/row cursor-pointer transition-colors hover:bg-secondary/30 focus-visible:bg-secondary/30 focus-visible:outline-none',
        task.is_completed && 'opacity-60',
        isSubtask && 'bg-secondary/10',
        isDragging && 'bg-card shadow-lg ring-2 ring-old-growth/40',
      )}
    >
      <TableCell className="py-2" onClick={stopRowOpen}>
        <div className="flex items-center gap-1">
          {draggableProvided ? (
            <button
              type="button"
              {...draggableProvided.dragHandleProps}
              aria-label={`Reorder ${task.name}`}
              className={cn(
                '-ml-1 inline-flex h-5 w-4 cursor-grab items-center justify-center rounded text-muted-foreground/40 transition-opacity hover:bg-secondary hover:text-foreground active:cursor-grabbing',
                disabled
                  ? 'invisible'
                  : 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100',
              )}
              onClick={(e) => e.stopPropagation()}
              tabIndex={disabled ? -1 : 0}
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={1.6} />
            </button>
          ) : (
            <span className="-ml-1 inline-block h-5 w-4" aria-hidden="true" />
          )}
          <Checkbox
            checked={task.is_completed}
            onCheckedChange={(v) => persist({ is_completed: v })}
            ariaLabel={
              task.is_completed ? 'Mark as not completed' : 'Mark as completed'
            }
            disabled={disabled}
          />
        </div>
      </TableCell>
      <TableCell className="min-w-0 py-2" onClick={stopRowOpen}>
        <div
          className={cn(
            'flex min-w-0 w-full gap-1',
            wrap ? 'items-start' : 'items-center',
            isSubtask && 'pl-6',
          )}
        >
          {isSubtask && (
            <CornerDownRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-muted-foreground/60',
                wrap && 'mt-2',
              )}
              aria-hidden="true"
            />
          )}
          {hasSubtasks && !isSubtask && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSubtasks?.();
              }}
              className={cn(
                '-ml-1 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                wrap && 'mt-1',
              )}
              aria-label={
                subtasksExpanded
                  ? `Collapse ${subtaskCount} subtasks`
                  : `Expand ${subtaskCount} subtasks`
              }
              aria-expanded={subtasksExpanded}
            >
              {subtasksExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          <TaskNameField
            value={name}
            onChange={setName}
            onBlur={onNameBlur}
            disabled={disabled}
            className={cn(
              task.is_completed && 'line-through',
              isSubtask && 'text-[13px]',
            )}
          />
          {!isSubtask && subtaskCount > 0 && (
            <Badge
              variant="muted"
              className={cn(
                'shrink-0 rounded-full px-1.5 py-0 text-[10px]',
                wrap && 'mt-1.5',
              )}
              title={`${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}`}
            >
              {subtaskCount}
            </Badge>
          )}
          {isLinked && !isSubtask && (
            <Badge
              variant="outline"
              className={cn(
                'shrink-0 rounded-full px-1.5 py-0 text-[10px] text-muted-foreground',
                wrap && 'mt-1.5',
              )}
              title={`Lives in “${task.project?.name ?? 'another project'}” — shown here because it was added to this project too.`}
            >
              <FolderInput className="mr-1 h-2.5 w-2.5" />
              {task.project?.name ?? 'Linked'}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <AssigneesPicker
          assignees={task.assignees}
          members={members}
          disabled={disabled}
          onChange={onAssigneesChange}
          triggerClassName="h-8 border-transparent bg-transparent px-2 hover:bg-secondary/40 hover:border-border focus:border-input focus:ring-0 focus:ring-offset-0"
        />
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <DatePicker
          value={task.due_date ?? ''}
          onChange={(next) => persist({ due_date: next || null })}
          disabled={disabled}
          aria-label={`Due date for ${task.name}`}
          className="h-8 border-transparent bg-transparent px-2 text-xs hover:border-border"
        />
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <WorkloadHoursInput
          value={task.approximate_workload_hours}
          onCommit={(next) => persist({ approximate_workload_hours: next })}
          disabled={disabled}
          className="h-8 border-transparent bg-transparent px-2 text-xs hover:border-border focus:border-input"
        />
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <PrioritySelect
          value={task.priority}
          disabled={disabled}
          onChange={(p) => persist({ priority: p })}
        />
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <StatusSelect
          value={taskStatusOption(task)}
          options={STATUS_OPTION_ORDER}
          disabled={disabled}
          onChange={(s) => persist(taskStatusPatch(task, s))}
        />
      </TableCell>
      <TableCell className="py-2" onClick={stopRowOpen}>
        <TaskActionsMenu
          task={task}
          isSubtask={isSubtask}
          hasSubtasks={hasSubtasks}
          isLinked={isLinked}
          disabled={disabled}
          sectionsList={sectionsList}
          allTasks={allTasks}
          onMoveToSection={onMoveToSection}
          onMakeSubtaskOf={onMakeSubtaskOf}
          onPromoteToMainTask={onPromoteToMainTask}
          otherProjects={otherProjects}
          onMoveToProject={onMoveToProject}
          onAddToProject={onAddToProject}
          onRemoveFromProject={onRemoveFromProject}
          onDelete={onDelete}
        />
      </TableCell>
      <TableCell className="py-2 pr-3 text-right">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          aria-label={`Open details for ${task.name}`}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Row-level kebab menu — Move to section / Make subtask / Promote / Delete
//
// Replaces the dedicated trash-icon column from the original layout
// with a single MoreHorizontal trigger that surfaces every "structural"
// edit (which bucket the task lives in) plus the destructive delete in
// one popover. The page owns the move callbacks because they need to
// look at the cached tasks + sections to compute the destination
// position — see ProjectDetailPage.moveTaskToSection and friends.
// ---------------------------------------------------------------------------

function TaskActionsMenu({
  task,
  isSubtask,
  hasSubtasks,
  isLinked,
  disabled,
  sectionsList,
  allTasks,
  onMoveToSection,
  onMakeSubtaskOf,
  onPromoteToMainTask,
  otherProjects,
  onMoveToProject,
  onAddToProject,
  onRemoveFromProject,
  onDelete,
}: {
  task: TaskWithAssignees;
  isSubtask: boolean;
  hasSubtasks: boolean;
  /**
   * `true` when the row's home is another project (it's multi-homed
   * into the page being viewed). Linked rows get a reduced menu —
   * structural moves belong to the home project's page; here the only
   * structural action is "Remove from this project" (drops the link)
   * plus the regular delete.
   */
  isLinked: boolean;
  disabled: boolean;
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  onMoveToSection: (task: TaskWithAssignees, sectionId: string | null) => void;
  onMakeSubtaskOf: (task: TaskWithAssignees, parentId: string) => void;
  onPromoteToMainTask: (task: TaskWithAssignees) => void;
  otherProjects: ProjectOption[];
  onMoveToProject: (task: TaskWithAssignees, projectId: string) => void;
  onAddToProject: (task: TaskWithAssignees, projectId: string) => void;
  onRemoveFromProject: (task: TaskWithAssignees) => void;
  onDelete: () => void;
}) {
  // "Make subtask of…" targets every top-level task in the project
  // except this one and (for a row that is already a subtask) its
  // current parent — re-parenting within the existing parent is a
  // no-op and promotion has its own first-class menu item.
  //
  // A row that already has subtasks of its own is blocked from
  // becoming a subtask: the DB caps nesting at one level via
  // tg_tasks_validate_parent and a depth-2 chain would otherwise
  // confuse the UI grouping.
  const candidateParents = useMemo(
    () =>
      allTasks.filter(
        (t) =>
          t.parent_task_id === null &&
          t.id !== task.id &&
          t.id !== task.parent_task_id &&
          // Same project (the cache only ever holds the current project's
          // rows, but this keeps the filter explicit + safe if that
          // assumption ever shifts).
          t.project_id === task.project_id,
      ),
    [allTasks, task.id, task.parent_task_id, task.project_id],
  );

  const showMakeSubtask = !hasSubtasks && candidateParents.length > 0;
  const showPromote = isSubtask;
  const currentSectionId = task.section_id ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          aria-label="Task actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onClick={(e) => e.stopPropagation()}
      >
        {isLinked ? (
          // A row whose home is another project: structural moves
          // (sections / subtasks / re-home) belong on the home
          // project's page. Here we only offer dropping the link.
          <>
            {!isSubtask && (
              <>
                <DropdownMenuItem onSelect={() => onRemoveFromProject(task)}>
                  <Unlink className="mr-2 h-3.5 w-3.5" />
                  Remove from this project
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete {isSubtask ? 'subtask' : 'task'} everywhere
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel>Move</DropdownMenuLabel>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ListFilter className="mr-2 h-3.5 w-3.5" />
                Move to section…
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                  <DropdownMenuItem
                    onSelect={() => onMoveToSection(task, null)}
                    disabled={!isSubtask && currentSectionId === null}
                  >
                    <span className="text-muted-foreground">No section</span>
                  </DropdownMenuItem>
                  {sectionsList.length > 0 && <DropdownMenuSeparator />}
                  {sectionsList.map((s) => (
                    <DropdownMenuItem
                      key={s.id}
                      onSelect={() => onMoveToSection(task, s.id)}
                      disabled={!isSubtask && currentSectionId === s.id}
                    >
                      <span className="truncate">{s.name}</span>
                    </DropdownMenuItem>
                  ))}
                  {sectionsList.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No sections yet.
                    </div>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {showMakeSubtask && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <ListTree className="mr-2 h-3.5 w-3.5" />
                  Make subtask of…
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-72 w-64 overflow-y-auto">
                    {candidateParents.map((p) => (
                      <DropdownMenuItem
                        key={p.id}
                        onSelect={() => onMakeSubtaskOf(task, p.id)}
                      >
                        <span className="truncate">{p.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}

            {showPromote && (
              <DropdownMenuItem onSelect={() => onPromoteToMainTask(task)}>
                <CornerLeftUp className="mr-2 h-3.5 w-3.5" />
                Promote to main task
              </DropdownMenuItem>
            )}

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderInput className="mr-2 h-3.5 w-3.5" />
                Move to project…
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-72 w-64 overflow-y-auto">
                  {otherProjects.map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      onSelect={() => onMoveToProject(task, p.id)}
                    >
                      <span className="truncate">{p.name}</span>
                    </DropdownMenuItem>
                  ))}
                  {otherProjects.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No other projects.
                    </div>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {/* Multi-homing is parent-task-only: subtasks inherit
                their parent's projects (the DB trigger rejects
                subtask links too). */}
            {!isSubtask && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <FolderPlus className="mr-2 h-3.5 w-3.5" />
                  Add to project…
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-72 w-64 overflow-y-auto">
                    {otherProjects.map((p) => (
                      <DropdownMenuItem
                        key={p.id}
                        onSelect={() => onAddToProject(task, p.id)}
                      >
                        <span className="truncate">{p.name}</span>
                      </DropdownMenuItem>
                    ))}
                    {otherProjects.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        No other projects.
                      </div>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete {isSubtask ? 'subtask' : 'task'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Inline cell editors
// ---------------------------------------------------------------------------

function PrioritySelect({
  value,
  disabled,
  onChange,
}: {
  value: TaskPriority;
  disabled: boolean;
  onChange: (next: TaskPriority) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as TaskPriority)}
      disabled={disabled}
    >
      <SelectTrigger
        className="h-8 w-full border-transparent bg-transparent px-2 hover:border-border focus:border-input"
        onClick={(e) => e.stopPropagation()}
      >
        <span>
          <span
            className={cn(
              'inline-flex max-w-full items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
              PRIORITY_META[value].className,
            )}
          >
            {PRIORITY_META[value].label}
          </span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {PRIORITY_ORDER.map((p) => (
          <SelectItem key={p} value={p}>
            {PRIORITY_META[p].label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Existing rows get the full option list including "Completed" (which
// maps to the checkmark — see taskStatusPatch). The new-task footer
// passes `options={STATUS_ORDER}` so a task can't be born completed.
function StatusSelect<T extends TaskStatusOption>({
  value,
  disabled,
  options,
  onChange,
}: {
  value: T;
  disabled: boolean;
  options: readonly T[];
  onChange: (next: T) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as T)}
      disabled={disabled}
    >
      <SelectTrigger
        className="h-8 w-full border-transparent bg-transparent px-2 hover:border-border focus:border-input"
        onClick={(e) => e.stopPropagation()}
      >
        <span>
          <span
            className={cn(
              'inline-flex max-w-full items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
              statusOptionMeta(value).className,
            )}
          >
            {statusOptionMeta(value).label}
          </span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {options.map((s) => (
          <SelectItem key={s} value={s}>
            {statusOptionMeta(s).label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// New-task row (Asana-style "+ Add task" inline footer)
//
// Drops a fresh top-level task into a specific section (or the implicit
// "(no section)" bucket when `sectionId` is null). Section membership
// is stamped at create-time and persisted via tasks.section_id —
// moving the task across sections later is a normal column update.
// ---------------------------------------------------------------------------

function NewTaskRow({
  projectId,
  members,
  disabled,
  sectionId,
}: {
  projectId: string;
  members: MemberLite[];
  disabled: boolean;
  sectionId: string | null;
}) {
  const createTask = useCreateTask();
  const [name, setName] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [workloadHours, setWorkloadHours] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [status, setStatus] = useState<TaskStatus>('not_started');

  // Materialise the picked ids back to MemberLite rows so the
  // AssigneesSelect trigger can render avatars without a parallel
  // lookup. Order matches `members` (stable) for visual consistency
  // with the saved rows.
  const draftAssignees = useMemo(() => {
    const wanted = new Set(assigneeIds);
    return members.filter((m) => wanted.has(m.id));
  }, [members, assigneeIds]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || createTask.isPending) return;
    let hours: number | null = null;
    try {
      hours = parseWorkloadHours(workloadHours);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid hours.');
      return;
    }
    createTask.mutate(
      {
        projectId,
        name: trimmed,
        assigneeIds,
        dueDate: dueDate || undefined,
        approximateWorkloadHours: hours,
        priority,
        status,
        sectionId,
      },
      {
        onSuccess: () => {
          setName('');
          setAssigneeIds([]);
          setDueDate('');
          setWorkloadHours('');
          setPriority('medium');
          setStatus('not_started');
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Could not add task';
          toast.error(msg);
        },
      },
    );
  };

  if (disabled) return null;

  return (
    <TableRow className="bg-secondary/20 hover:bg-secondary/30">
      <TableCell className="py-2">
        <Plus className="h-4 w-4 text-muted-foreground" />
      </TableCell>
      <TableCell className="min-w-0 py-2">
        <TaskNameField
          value={name}
          onChange={setName}
          placeholder="Add task…"
          onEnter={submit}
        />
      </TableCell>
      <TableCell className="py-2">
        <AssigneesPicker
          assignees={draftAssignees}
          members={members}
          onChange={setAssigneeIds}
          disabled={false}
          triggerClassName="h-8 border-transparent bg-transparent px-2 hover:bg-secondary/40 hover:border-border focus:border-input focus:ring-0 focus:ring-offset-0"
        />
      </TableCell>
      <TableCell className="py-2">
        <DatePicker
          value={dueDate}
          onChange={setDueDate}
          aria-label="Due date"
          className="h-8 border-transparent bg-transparent px-2 text-xs hover:border-border"
        />
      </TableCell>
      <TableCell className="py-2">
        <WorkloadHoursDraftInput
          value={workloadHours}
          onChange={setWorkloadHours}
          className="h-8 border-transparent bg-transparent px-2 text-xs hover:border-border focus:border-input"
        />
      </TableCell>
      <TableCell className="py-2">
        <PrioritySelect value={priority} onChange={setPriority} disabled={false} />
      </TableCell>
      <TableCell className="py-2">
        <StatusSelect
          value={status}
          options={STATUS_ORDER}
          onChange={setStatus}
          disabled={false}
        />
      </TableCell>
      <TableCell className="py-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={!name.trim() || createTask.isPending}
        >
          {createTask.isPending ? 'Adding…' : 'Add'}
        </Button>
      </TableCell>
      <TableCell className="py-2" />
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// New-subtask row — inline composer that appears under an expanded parent
//
// Lighter than NewTaskRow on purpose: subtasks default to the parent's
// implicit context (same project, no section, parent's vibe) so we
// only collect a name here. The user can fine-tune assignees / dates /
// priority / status from the subtask's own row after it's created, or
// from the side drawer.
// ---------------------------------------------------------------------------

function NewSubtaskRow({
  projectId,
  parentTaskId,
  disabled,
}: {
  projectId: string;
  parentTaskId: string;
  disabled: boolean;
}) {
  const createTask = useCreateTask();
  const [name, setName] = useState('');
  const wrap = useContext(WrapTaskNameContext);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || createTask.isPending) return;
    createTask.mutate(
      { projectId, name: trimmed, parentTaskId },
      {
        onSuccess: () => setName(''),
        onError: (err) => {
          const msg =
            err instanceof Error ? err.message : 'Could not add subtask';
          toast.error(msg);
        },
      },
    );
  };

  if (disabled) return null;

  return (
    <TableRow className="bg-secondary/10 hover:bg-secondary/20">
      <TableCell className="py-1.5" />
      <TableCell className="min-w-0 py-1.5" colSpan={TASK_COL_COUNT - 1}>
        <div className={cn('flex gap-2 pl-6', wrap ? 'items-start' : 'items-center')}>
          <Plus
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground',
              wrap && 'mt-2',
            )}
          />
          <TaskNameField
            value={name}
            onChange={setName}
            placeholder="Add subtask…"
            onEnter={submit}
            className="text-[13px]"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={submit}
            disabled={!name.trim() || createTask.isPending}
            className="h-7"
          >
            {createTask.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Section group — header + parent rows + subtasks + per-section new-task row
//
// One <SectionGroup> renders a whole bucket: the header (when there is
// one), every parent + subtask row in the bucket, and the inline "+
// Add task" footer. The unsectioned bucket renders without a header
// row so projects that never created a section look identical to
// today.
// ---------------------------------------------------------------------------

function SectionGroup({
  section,
  tasks,
  linkedTasks,
  totalTaskCount,
  members,
  disabled,
  projectId,
  collapsed,
  onToggleCollapse,
  onDeleteSection,
  onOpenTask,
  subtasksByParent,
  visibleSubtasksByParent,
  expandedSubtasks,
  onToggleSubtasks,
  sectionsList,
  allTasks,
  canMoveSectionUp,
  canMoveSectionDown,
  onMoveSectionUp,
  onMoveSectionDown,
  onMoveTaskToSection,
  onMakeTaskSubtaskOf,
  onPromoteSubtaskToMain,
  otherProjects,
  onMoveTaskToProject,
  onAddTaskToProject,
  onRemoveTaskFromProject,
  hideComposers,
}: {
  section: ProjectSection | null;
  tasks: TaskWithAssignees[];
  /**
   * Tasks multi-homed into this project from another one (their
   * `project_id` is foreign). Rendered after the draggable rows as
   * non-draggable entries so they can't disturb the reorder math —
   * the page only passes them for the unsectioned bucket.
   */
  linkedTasks: TaskWithAssignees[];
  totalTaskCount: number;
  members: MemberLite[];
  disabled: boolean;
  projectId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onDeleteSection: () => void;
  onOpenTask: (t: TaskWithAssignees) => void;
  subtasksByParent: Map<string, TaskWithAssignees[]>;
  visibleSubtasksByParent: Map<string, TaskWithAssignees[]>;
  expandedSubtasks: Set<string>;
  onToggleSubtasks: (parentId: string) => void;
  /**
   * True while any task row is mid-drag. The inline "Add subtask…"
   * composer rows aren't Draggables, so the library can't displace
   * them while rows shuffle — we hide them for the drag instead.
   */
  hideComposers: boolean;
  /**
   * Project-wide context the kebab move menu on each row needs:
   * `sectionsList` powers the "Move to section…" submenu and
   * `allTasks` is used to derive the list of legal parents for
   * "Make subtask of…" without re-filtering for every row.
   */
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  canMoveSectionUp: boolean;
  canMoveSectionDown: boolean;
  onMoveSectionUp: () => void;
  onMoveSectionDown: () => void;
  onMoveTaskToSection: (task: TaskWithAssignees, sectionId: string | null) => void;
  onMakeTaskSubtaskOf: (task: TaskWithAssignees, parentId: string) => void;
  onPromoteSubtaskToMain: (task: TaskWithAssignees) => void;
  otherProjects: ProjectOption[];
  onMoveTaskToProject: (task: TaskWithAssignees, projectId: string) => void;
  onAddTaskToProject: (task: TaskWithAssignees, projectId: string) => void;
  onRemoveTaskFromProject: (task: TaskWithAssignees) => void;
}) {
  // Each bucket is rendered as a stack of <tbody> elements:
  //
  //   1. Section header (its own droppable so the header can be the
  //      drag target/source for section reorder). Skipped for the
  //      unsectioned bucket which doesn't have a header.
  //   2. Task rows (one droppable per bucket, type="TASK"). Parents
  //      and their expanded subtasks are flattened into a single
  //      draggable list so a drag can cross hierarchy levels: drop a
  //      subtask among top-level rows to promote it, drop it inside
  //      another parent's block to re-parent it, or drop a childless
  //      top-level task into a subtask block to demote it. The flat
  //      indexes come from flatBucketRows so they line up with the
  //      page-level onDragEnd math.
  //   3. The "+ Add task" footer (own tbody, not draggable).
  //
  // We deliberately keep section reordering AND the existing up/down
  // arrow buttons in the header: drag is the headline affordance, but
  // the arrows are a keyboard-accessible / non-mouse fallback.
  const flatRows = flatBucketRows(
    tasks,
    visibleSubtasksByParent,
    expandedSubtasks,
  );
  const flatIndexById = new Map<string, number>();
  flatRows.forEach((entry, idx) => flatIndexById.set(entry.task.id, idx));

  return (
    <>
      {section && !disabled ? (
        <Droppable
          droppableId={sectionDroppableId(section.id)}
          type="SECTION"
        >
          {(dropProvided) => (
            <tbody
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
            >
              <Draggable draggableId={section.id} index={0}>
                {(dragProvided, snapshot) => (
                  <SectionHeaderRow
                    section={section}
                    taskCount={totalTaskCount}
                    collapsed={collapsed}
                    onToggle={onToggleCollapse}
                    onDelete={onDeleteSection}
                    disabled={disabled}
                    canMoveUp={canMoveSectionUp}
                    canMoveDown={canMoveSectionDown}
                    onMoveUp={onMoveSectionUp}
                    onMoveDown={onMoveSectionDown}
                    draggableProvided={dragProvided}
                    isDragging={snapshot.isDragging}
                  />
                )}
              </Draggable>
              {dropProvided.placeholder}
            </tbody>
          )}
        </Droppable>
      ) : section ? (
        <tbody>
          <SectionHeaderRow
            section={section}
            taskCount={totalTaskCount}
            collapsed={collapsed}
            onToggle={onToggleCollapse}
            onDelete={onDeleteSection}
            disabled={disabled}
            canMoveUp={canMoveSectionUp}
            canMoveDown={canMoveSectionDown}
            onMoveUp={onMoveSectionUp}
            onMoveDown={onMoveSectionDown}
            draggableProvided={null}
            isDragging={false}
          />
        </tbody>
      ) : null}
      {!collapsed && (
        <Droppable
          droppableId={bucketDroppableId(section?.id ?? null)}
          type="TASK"
          isDropDisabled={disabled}
        >
          {(dropProvided) => (
            <tbody
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
            >
              {/*
                Empty buckets render with a zero-height tbody, which
                makes it nearly impossible to physically aim a drag at
                them — the cursor has to land in the gap between the
                section header and the "+ Add task" footer. We render
                a single-row hint so the droppable has real estate to
                hit-test against, and so the user gets a visible
                "Drop tasks here" cue. Suppressed for the unsectioned
                bucket on a brand-new project (handled by the page-
                level "No tasks yet" state above).
              */}
              {tasks.length === 0 && section && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={TASK_COL_COUNT}
                    className="py-4 text-center text-xs italic text-muted-foreground"
                  >
                    {disabled
                      ? 'No tasks in this section.'
                      : 'No tasks yet.'}
                  </TableCell>
                </TableRow>
              )}
              {tasks.map((task) => {
                const allChildren = subtasksByParent.get(task.id) ?? [];
                const visibleChildren =
                  visibleSubtasksByParent.get(task.id) ?? [];
                const isExpanded = expandedSubtasks.has(task.id);
                return (
                  <Fragment key={task.id}>
                    <Draggable
                      draggableId={task.id}
                      index={flatIndexById.get(task.id)!}
                      isDragDisabled={disabled}
                    >
                      {(dragProvided, snapshot) => (
                        <TaskRow
                          task={task}
                          members={members}
                          disabled={disabled}
                          onOpen={() => onOpenTask(task)}
                          subtaskCount={allChildren.length}
                          hasSubtasks={allChildren.length > 0}
                          subtasksExpanded={isExpanded}
                          onToggleSubtasks={() => onToggleSubtasks(task.id)}
                          currentProjectId={projectId}
                          sectionsList={sectionsList}
                          allTasks={allTasks}
                          onMoveToSection={onMoveTaskToSection}
                          onMakeSubtaskOf={onMakeTaskSubtaskOf}
                          onPromoteToMainTask={onPromoteSubtaskToMain}
                          otherProjects={otherProjects}
                          onMoveToProject={onMoveTaskToProject}
                          onAddToProject={onAddTaskToProject}
                          onRemoveFromProject={onRemoveTaskFromProject}
                          draggableProvided={dragProvided}
                          isDragging={snapshot.isDragging}
                        />
                      )}
                    </Draggable>
                    {isExpanded &&
                      visibleChildren.map((child) => (
                        <Draggable
                          key={child.id}
                          draggableId={child.id}
                          index={flatIndexById.get(child.id)!}
                          isDragDisabled={disabled}
                        >
                          {(dragProvided, snapshot) => (
                            <TaskRow
                              task={child}
                              members={members}
                              disabled={disabled}
                              onOpen={() => onOpenTask(child)}
                              isSubtask
                              currentProjectId={projectId}
                              sectionsList={sectionsList}
                              allTasks={allTasks}
                              onMoveToSection={onMoveTaskToSection}
                              onMakeSubtaskOf={onMakeTaskSubtaskOf}
                              onPromoteToMainTask={onPromoteSubtaskToMain}
                              otherProjects={otherProjects}
                              onMoveToProject={onMoveTaskToProject}
                              onAddToProject={onAddTaskToProject}
                              onRemoveFromProject={onRemoveTaskFromProject}
                              draggableProvided={dragProvided}
                              isDragging={snapshot.isDragging}
                            />
                          )}
                        </Draggable>
                      ))}
                    {isExpanded && !hideComposers && (
                      <NewSubtaskRow
                        projectId={projectId}
                        parentTaskId={task.id}
                        disabled={disabled}
                      />
                    )}
                  </Fragment>
                );
              })}
              {dropProvided.placeholder}
              {/*
                Multi-homed rows from other projects render after the
                draggable list (outside its index space). They keep
                their expandable subtask groups but no inline subtask
                composer — those edits route through the drawer, which
                knows the task's home project.
              */}
              {linkedTasks.map((task) => {
                const allChildren = subtasksByParent.get(task.id) ?? [];
                const visibleChildren =
                  visibleSubtasksByParent.get(task.id) ?? [];
                const isExpanded = expandedSubtasks.has(task.id);
                return (
                  <Fragment key={task.id}>
                    <TaskRow
                      task={task}
                      members={members}
                      disabled={disabled}
                      onOpen={() => onOpenTask(task)}
                      subtaskCount={allChildren.length}
                      hasSubtasks={allChildren.length > 0}
                      subtasksExpanded={isExpanded}
                      onToggleSubtasks={() => onToggleSubtasks(task.id)}
                      currentProjectId={projectId}
                      sectionsList={sectionsList}
                      allTasks={allTasks}
                      onMoveToSection={onMoveTaskToSection}
                      onMakeSubtaskOf={onMakeTaskSubtaskOf}
                      onPromoteToMainTask={onPromoteSubtaskToMain}
                      otherProjects={otherProjects}
                      onMoveToProject={onMoveTaskToProject}
                      onAddToProject={onAddTaskToProject}
                      onRemoveFromProject={onRemoveTaskFromProject}
                      draggableProvided={null}
                    />
                    {isExpanded &&
                      visibleChildren.map((child) => (
                        <TaskRow
                          key={child.id}
                          task={child}
                          members={members}
                          disabled={disabled}
                          onOpen={() => onOpenTask(child)}
                          isSubtask
                          currentProjectId={projectId}
                          sectionsList={sectionsList}
                          allTasks={allTasks}
                          onMoveToSection={onMoveTaskToSection}
                          onMakeSubtaskOf={onMakeTaskSubtaskOf}
                          onPromoteToMainTask={onPromoteSubtaskToMain}
                          otherProjects={otherProjects}
                          onMoveToProject={onMoveTaskToProject}
                          onAddToProject={onAddTaskToProject}
                          onRemoveFromProject={onRemoveTaskFromProject}
                          draggableProvided={null}
                        />
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          )}
        </Droppable>
      )}
      {!collapsed && (
        <tbody>
          <NewTaskRow
            projectId={projectId}
            members={members}
            disabled={disabled}
            sectionId={section?.id ?? null}
          />
        </tbody>
      )}
    </>
  );
}

function SectionHeaderRow({
  section,
  taskCount,
  collapsed,
  onToggle,
  onDelete,
  disabled,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  draggableProvided,
  isDragging,
}: {
  section: ProjectSection;
  taskCount: number;
  collapsed: boolean;
  onToggle: () => void;
  onDelete: () => void;
  disabled: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /**
   * `@hello-pangea/dnd` draggable provided object. When present, the
   * grip handle on the left becomes the drag handle and the row is
   * wired up as a draggable. `null` when the bucket is archived /
   * read-only (we don't want a grip you can't actually grab).
   */
  draggableProvided: DraggableProvided | null;
  isDragging: boolean;
}) {
  const updateSection = useUpdateProjectSection();
  const [name, setName] = useState(section.name);
  useEffect(() => setName(section.name), [section.name]);

  const onNameBlur = () => {
    const trimmed = name.trim();
    if (trimmed === section.name) return;
    if (!trimmed) {
      setName(section.name);
      toast.error('Section name cannot be empty.');
      return;
    }
    updateSection.mutate(
      { id: section.id, projectId: section.project_id, patch: { name: trimmed } },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Rename failed';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <TableRow
      ref={draggableProvided?.innerRef}
      {...(draggableProvided?.draggableProps ?? {})}
      className={cn(
        'group/section border-b-0 bg-secondary/40 hover:bg-secondary/40',
        isDragging && 'bg-card shadow-lg ring-2 ring-old-growth/40',
      )}
    >
      <TableCell colSpan={TASK_COL_COUNT} className="py-2">
        <div className="flex items-center gap-2">
          {draggableProvided ? (
            <button
              type="button"
              {...draggableProvided.dragHandleProps}
              aria-label={`Reorder section ${section.name}`}
              className="-ml-1 inline-flex h-6 w-5 cursor-grab items-center justify-center rounded text-muted-foreground/50 opacity-0 transition-opacity hover:bg-secondary hover:text-foreground active:cursor-grabbing group-hover/section:opacity-100 focus-visible:opacity-100"
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={1.6} />
            </button>
          ) : (
            <span className="-ml-1 inline-block h-6 w-5" aria-hidden="true" />
          )}
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label={collapsed ? 'Expand section' : 'Collapse section'}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <span aria-hidden className="h-4 w-1 shrink-0 rounded-full bg-info" />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={onNameBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            disabled={disabled}
            className="h-8 max-w-md border-transparent bg-transparent px-2 text-xs font-medium uppercase tracking-wider text-info hover:border-border focus:border-input"
            aria-label="Section name"
          />
          <Badge variant="info-soft" className="rounded-full px-2 py-0 tabular-nums">
            {taskCount}
          </Badge>
          <div className="flex-1" />
          {!disabled && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                onClick={onMoveUp}
                disabled={!canMoveUp}
                aria-label="Move section up"
                title="Move section up"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                onClick={onMoveDown}
                disabled={!canMoveDown}
                aria-label="Move section down"
                title="Move section down"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete section"
                title="Delete section (tasks fall back to unsectioned)"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// "+ Add section" — single row at the bottom of the table that turns
// into an inline input on click. We use a row (rather than a button
// floating outside the table) so the affordance lines up visually with
// the existing "+ Add task" footers.
// ---------------------------------------------------------------------------

function AddSectionRow({
  projectId,
  disabled,
}: {
  projectId: string;
  disabled: boolean;
}) {
  const createSection = useCreateProjectSection();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');

  if (disabled) return null;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setEditing(false);
      setName('');
      return;
    }
    try {
      await createSection.mutateAsync({ projectId, name: trimmed });
      setName('');
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not add section';
      toast.error(msg);
    }
  };

  return (
    <TableRow className="hover:bg-secondary/20">
      <TableCell colSpan={TASK_COL_COUNT} className="py-2">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
                if (e.key === 'Escape') {
                  setName('');
                  setEditing(false);
                }
              }}
              onBlur={() => void submit()}
              placeholder="Section name…"
              className="h-8 max-w-md text-sm"
            />
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={!name.trim() || createSection.isPending}
            >
              {createSection.isPending ? 'Adding…' : 'Add section'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setName('');
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Add section
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Delete section confirmation
//
// Section deletion is non-destructive for the tasks inside it: the
// ON DELETE SET NULL on tasks.section_id means each affected task
// falls back into the "(no section)" bucket at the top of the
// project table. We surface that explicitly in the dialog body so
// the operator knows their work isn't going anywhere.
// ---------------------------------------------------------------------------

function DeleteSectionDialog({
  section,
  projectId,
  onOpenChange,
}: {
  section: ProjectSection | null;
  projectId: string;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteSection = useDeleteProjectSection();
  const open = !!section;

  const onConfirm = async () => {
    if (!section) return;
    try {
      await deleteSection.mutateAsync({ id: section.id, projectId });
      toast.success(`Deleted section “${section.name}”`);
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete section?</DialogTitle>
          <DialogDescription>
            {section ? (
              <>
                Remove the section{' '}
                <span className="font-medium text-foreground">
                  “{section.name}”
                </span>
                . Tasks inside move to the unsectioned area — they won&apos;t be
                deleted.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteSection.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleteSection.isPending}
          >
            {deleteSection.isPending ? 'Deleting…' : 'Delete section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Share dialog
// ---------------------------------------------------------------------------

function ShareDialog({
  open,
  onOpenChange,
  projectId,
  creatorId,
  members,
  canManage,
  allProfiles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  creatorId: string | null;
  members: MemberLite[];
  canManage: boolean;
  allProfiles: MemberLite[];
}) {
  const addMembers = useAddProjectMembers();
  const removeMember = useRemoveProjectMember();
  const [draftIds, setDraftIds] = useState<string[]>([]);

  // Reset the picker whenever the dialog opens so a previously-typed
  // draft doesn't survive a Cancel.
  useEffect(() => {
    if (open) setDraftIds([]);
  }, [open]);

  const memberIdSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const addable = allProfiles.filter((p) => !memberIdSet.has(p.id));

  const onAdd = async () => {
    if (draftIds.length === 0) return;
    try {
      await addMembers.mutateAsync({ projectId, profileIds: draftIds });
      toast.success(
        draftIds.length === 1
          ? 'Added 1 person'
          : `Added ${draftIds.length} people`,
      );
      setDraftIds([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to share';
      toast.error(msg);
    }
  };

  const onRemove = async (profileId: string) => {
    try {
      await removeMember.mutateAsync({ projectId, profileId });
      toast.success('Removed from project');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto"
        // Block only focus-outside dismissal so switching browser tabs
        // (which fires focus-outside) doesn't discard in-progress
        // membership changes. The X, Escape, and outside clicks still
        // close it.
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Share project</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {canManage && (
            <div className="space-y-2">
              <PeoplePicker
                profiles={addable}
                value={draftIds}
                onChange={setDraftIds}
                placeholder={
                  addable.length === 0
                    ? 'Everyone is already shared'
                    : 'Pick people to add…'
                }
                disabled={addable.length === 0}
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={onAdd}
                  disabled={draftIds.length === 0 || addMembers.isPending}
                >
                  {addMembers.isPending ? 'Adding…' : 'Add'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Current members
            </div>
            <ul className="divide-y divide-border rounded-md border border-border">
              {members.map((m) => {
                const isCreator = creatorId === m.id;
                return (
                  <li
                    key={m.id}
                    className="flex items-center justify-between px-3 py-2"
                  >
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-7 w-7">
                        {m.avatar_url && <AvatarImage src={m.avatar_url} alt="" />}
                        <AvatarFallback className="text-[10px]">
                          {initialsOf(m)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm text-foreground">
                          {m.full_name ?? m.email}
                          {isCreator && (
                            <Badge variant="muted" className="ml-2">
                              Owner
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {m.email}
                        </div>
                      </div>
                    </div>
                    {canManage && !isCreator && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(m.id)}
                        disabled={removeMember.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                );
              })}
              {members.length === 0 && (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  No members yet.
                </li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ProjectDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-6 w-96" />
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}
